import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Grid, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, Tooltip, FormControl, InputLabel, Select, MenuItem,
  Accordion, AccordionSummary, AccordionDetails, Divider, FormControlLabel, Checkbox,
  Snackbar, Autocomplete, Switch, Menu, Popover,
} from '@mui/material';

// Common North American timezones, plus a curated list of other regions.
// Falls back to Intl.supportedValuesOf('timeZone') if available so the full
// IANA list is reachable for venues outside North America.
const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Asia/Tokyo',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Australia/Sydney',
  'UTC',
];

const STATE_TZ = {
  AL:'America/Chicago',AK:'America/Anchorage',AZ:'America/Phoenix',AR:'America/Chicago',
  CA:'America/Los_Angeles',CO:'America/Denver',CT:'America/New_York',DE:'America/New_York',
  FL:'America/New_York',GA:'America/New_York',HI:'Pacific/Honolulu',ID:'America/Boise',
  IL:'America/Chicago',IN:'America/Indiana/Indianapolis',IA:'America/Chicago',KS:'America/Chicago',
  KY:'America/New_York',LA:'America/Chicago',ME:'America/New_York',MD:'America/New_York',
  MA:'America/New_York',MI:'America/Detroit',MN:'America/Chicago',MS:'America/Chicago',
  MO:'America/Chicago',MT:'America/Denver',NE:'America/Chicago',NV:'America/Los_Angeles',
  NH:'America/New_York',NJ:'America/New_York',NM:'America/Denver',NY:'America/New_York',
  NC:'America/New_York',ND:'America/Chicago',OH:'America/New_York',OK:'America/Chicago',
  OR:'America/Los_Angeles',PA:'America/New_York',RI:'America/New_York',SC:'America/New_York',
  SD:'America/Chicago',TN:'America/Chicago',TX:'America/Chicago',UT:'America/Denver',
  VT:'America/New_York',VA:'America/New_York',WA:'America/Los_Angeles',WV:'America/New_York',
  WI:'America/Chicago',WY:'America/Denver',DC:'America/New_York',
};
function guessTimezone(state, country) {
  if (!state) return null;
  const co = (country || 'US').toUpperCase();
  if (co === 'US' || co === 'USA' || co === 'UNITED STATES') {
    const abbr = state.trim().toUpperCase().replace(/\.$/, '');
    if (STATE_TZ[abbr]) return STATE_TZ[abbr];
    const full = Object.entries({
      'CALIFORNIA':'CA','NEW YORK':'NY','TEXAS':'TX','FLORIDA':'FL','ILLINOIS':'IL',
      'PENNSYLVANIA':'PA','OHIO':'OH','GEORGIA':'GA','MICHIGAN':'MI','NORTH CAROLINA':'NC',
      'NEW JERSEY':'NJ','VIRGINIA':'VA','WASHINGTON':'WA','ARIZONA':'AZ','MASSACHUSETTS':'MA',
      'TENNESSEE':'TN','INDIANA':'IN','MARYLAND':'MD','MISSOURI':'MO','WISCONSIN':'WI',
      'COLORADO':'CO','MINNESOTA':'MN','SOUTH CAROLINA':'SC','ALABAMA':'AL','LOUISIANA':'LA',
      'KENTUCKY':'KY','OREGON':'OR','OKLAHOMA':'OK','CONNECTICUT':'CT','UTAH':'UT',
      'IOWA':'IA','NEVADA':'NV','ARKANSAS':'AR','MISSISSIPPI':'MS','KANSAS':'KS',
      'NEW MEXICO':'NM','NEBRASKA':'NE','HAWAII':'HI','IDAHO':'ID','MONTANA':'MT',
      'ALASKA':'AK','MAINE':'ME','RHODE ISLAND':'RI','DELAWARE':'DE','VERMONT':'VT',
      'WYOMING':'WY','NORTH DAKOTA':'ND','SOUTH DAKOTA':'SD','NEW HAMPSHIRE':'NH',
      'WEST VIRGINIA':'WV',
    }).find(([name]) => name === abbr);
    if (full) return STATE_TZ[full[1]];
  }
  if (co === 'GB' || co === 'UK') return 'Europe/London';
  if (co === 'FR' || co === 'FRANCE') return 'Europe/Paris';
  if (co === 'DE' || co === 'GERMANY') return 'Europe/Berlin';
  if (co === 'AU' || co === 'AUSTRALIA') return 'Australia/Sydney';
  if (co === 'JP' || co === 'JAPAN') return 'Asia/Tokyo';
  if (co === 'CA' || co === 'CANADA') return 'America/Toronto';
  return null;
}

const ALL_TIMEZONES = (() => {
  try {
    if (typeof Intl !== 'undefined' && Intl.supportedValuesOf) {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch (_) { /* fall through */ }
  return COMMON_TIMEZONES;
})();
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Upload as UploadIcon, ExpandMore as ExpandMoreIcon,
  Theaters as TheaterIcon, Tv as ScreenIcon, Refresh as RefreshIcon,
  RateReview as OutcomeIcon,
  Send as SendIcon, ContentCopy as CopyIcon, AssignmentTurnedIn as CompleteIcon,
  RestoreFromTrash as RestoreIcon, DeleteForever as DeleteForeverIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { formatRelativeTime } from '../utils/library';
import { formatCertExpiry, formatCertDate, describeCertIssuer } from '../utils/certFormat';
import {
  projectorLabel, serverLabel, filterEquipment,
  countPrefilledRequestFields,
} from '../utils/venues';
import EmailComposer from '../components/EmailComposer';
import RecordOutcomeDialog from '../components/RecordOutcomeDialog';
import PageHeader from '../components/PageHeader';
import PipelineProgress from '../components/PipelineProgress';
import VenuesTableView from '../components/VenuesTableView';
import VenuesBulkRequestDialog from '../components/VenuesBulkRequestDialog';
import {
  fetchVenues, createVenue, updateVenue, createScreen, updateScreen, uploadScreenCertificate, deleteVenue, restoreVenue, purgeVenue,
} from '../store/slices/venuesSlice';

const SCREEN_TYPES = ['Standard', 'IMAX', 'Dolby Cinema', 'PLF', 'Drive-In', 'Other'];
// (Removed in v10.4.27: parked PROJECTOR_MAKES + SERVER_MAKES arrays —
// the screen dialog now uses the live equipment-catalog dropdowns
// fetched from /api/equipment/, which superseded the static list.
// Recoverable from git if a future fallback path needs them.)

function VenueCertificates() {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const { venues, error } = useSelector((state) => state.venues);

  const [venueDialog, setVenueDialog] = useState(null);
  const [venueSaveError, setVenueSaveError] = useState('');
  const [screenDialog, setScreenDialog] = useState(null);
  const [certUpload, setCertUpload] = useState(null);
  const [certFile, setCertFile] = useState(null);
  const [requestLinkDialog, setRequestLinkDialog] = useState(false);
  const [requestForm, setRequestForm] = useState({
    title: '', recipient_name: '', recipient_email: '', expires_days: 14,
    first_screening_at: '', last_screening_at: '', send_email: true,
    // Optional pre-fill (operator fills in whatever they know; venue verifies)
    cc_email: '',
    chain: '', venue_name: '', screen_names: [''], server_info: '', projector_info: '',
    server_serial: '', street_address: '', city: '', state: '', country: 'US', zip_code: '',
    tech_contact_phone: '', timezone: '', version: '', resolution_required: '',
  });
  const [requestLinkResult, setRequestLinkResult] = useState(null);
  const [requestLinks, setRequestLinks] = useState([]);
  const [copied, setCopied] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Table-mode selection state (v10.19.50). Lives on the page so the
  // bulk-action bar above the grid can read it; the DataGrid drives it.
  const [tableSelection, setTableSelection] = useState([]);
  const [bulkRequestDialog, setBulkRequestDialog] = useState(false);
  // Filter / sort state for the venue list (client-side, not persisted).
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [chainFilter, setChainFilter] = useState('all');
  const [certFilter, setCertFilter] = useState('all');
  const [sortBy, setSortBy] = useState('chain');
  const [optionsAnchor, setOptionsAnchor] = useState(null);
  useEffect(() => { if (requestLinkDialog && userInfo?.email && !requestForm.cc_email) setRequestForm(f => ({ ...f, cc_email: userInfo.email })); }, [requestLinkDialog, userInfo?.email]);

  const [venueForm, setVenueForm] = useState({
    name: '', chain: '', city: '', state: '', country: 'US', zip_code: '', address: '',
    timezone: '', tech_contact_name: '', tech_contact_email: '', tech_contact_phone: '',
    booking_contact_name: '', booking_contact_email: '', notes: '',
    // Delivery profile — operator-set after observing how prior deliveries
    // to this venue went. Drives the format default in DeliverDialog and
    // surfaces "ask first" prompts when missing.
    preferred_format: '', ingest_capability: '', responsiveness: '',
    ingest_software: '', kdm_method_preferred: '',
  });

  // Outcomes timeline for the venue currently being edited. Pulled when
  // the dialog opens; null = "not yet fetched / not applicable".
  const [venueOutcomes, setVenueOutcomes] = useState(null);
  // v10.18.26: audit-log reconstruction of past KDM email events for
  // this venue. Surfaces deliveries whose kdm_records rows have been
  // hard-deleted (parent DCP cancelled), so the operator still sees
  // the historical send on the venue card.
  const [venuePastDeliveries, setVenuePastDeliveries] = useState(null);
  // Email composer state — { venueId, label } when opened from a venue
  // action; null = closed.
  const [venueComposer, setVenueComposer] = useState(null);
  // Record-outcome dialog state — { venueId, label } when invoked from
  // the venue page (no specific share/DCP context; operator backfilling
  // delivery history). The RecordOutcomeDialog accepts a bare venue
  // and lets the operator pick format + outcome + notes manually.
  const [venueOutcomeDialog, setVenueOutcomeDialog] = useState(null);

  const [screenForm, setScreenForm] = useState({
    name: '', screen_type: '', projector_make: '', projector_model: '',
    projector_serial: '', projector_resolution: '',
    is_3d: false, is_laser: false, is_hfr: false,
    max_audio_channels: 8,
    server_make: '', server_model: '', server_serial: '',
    server_firmware_version: '',
    audio_system_type: '',
    subtitle_capability: [],
    aspect_ratios_supported: [],
    three_d_system: '',
    notes: '',
    cert_not_required: false,
  });

  // Show-archived toggle: include soft-deleted venues in the list when on.
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    dispatch(fetchVenues({ includeArchived: showArchived }));
    handleLoadLinks();
  }, [dispatch, showArchived]);

  // Pick up ?complete=<id> / ?fulfill=<id> from the URL — the
  // OutstandingVenueRequestsTable on /distribution navigates here
  // with that param when the operator clicks Complete-with-cert or
  // Fulfill-manually. We open the matching dialog after requestLinks
  // arrives, then strip the param so a refresh doesn't reopen.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!requestLinks?.length) return;
    const completeId = searchParams.get('complete');
    const fulfillId = searchParams.get('fulfill');
    if (completeId) {
      const r = requestLinks.find(x => String(x.id) === String(completeId));
      if (r) {
        openCompleteRequest(r);
        setSearchParams((sp) => { sp.delete('complete'); return sp; });
      }
    } else if (fulfillId) {
      const r = requestLinks.find(x => String(x.id) === String(fulfillId));
      if (r) {
        fulfillRequest(r);
        setSearchParams((sp) => { sp.delete('fulfill'); return sp; });
      }
    }
    // openCompleteRequest / fulfillRequest are stable function identities
    // declared lower in the component; the eslint-disable keeps the
    // useEffect deps array from forcing a closure refresh on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestLinks, searchParams]);

  // Equipment catalog (loaded once, used by the screen-creation autocompletes)
  const [projectorCatalog, setProjectorCatalog] = useState([]);
  const [serverCatalog, setServerCatalog] = useState([]);
  useEffect(() => {
    // Surface catalog fetch failures so the operator knows why the
    // projector/server dropdowns in the screen dialog are empty.
    // Silent .catch made debugging "why no models showing" awful.
    Promise.allSettled([
      api.get('/api/equipment/projectors').then((r) => setProjectorCatalog(r.data?.items || [])),
      api.get('/api/equipment/servers').then((r) => setServerCatalog(r.data?.items || [])),
    ]).then((results) => {
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        setSnackbar({
          open: true,
          message: `Couldn't load ${failed === 2 ? 'equipment catalogs' : (results[0].status === 'rejected' ? 'projector catalog' : 'server catalog')} — projector/server dropdowns will be empty.`,
          severity: 'warning',
        });
      }
    });
  }, []);
  // (projectorLabel / serverLabel / filterEquipment moved to utils/venues.js
  // in v10.4.29 — same body, single tested source. Local alias kept so
  // the catalogs render the same shape they did before.)
  const applyProjectorPick = (p) => {
    if (!p) return;
    setScreenForm(f => ({
      ...f,
      projector_make: p.make || f.projector_make,
      projector_model: p.model || f.projector_model,
      projector_resolution: p.native_resolution || f.projector_resolution,
      is_laser: typeof p.is_laser === 'boolean' ? p.is_laser : f.is_laser,
      is_3d: typeof p.is_3d === 'boolean' ? p.is_3d : f.is_3d,
      is_hfr: typeof p.is_hfr === 'boolean' ? p.is_hfr : f.is_hfr,
    }));
  };
  const applyServerPick = (s) => {
    if (!s) return;
    setScreenForm(f => ({
      ...f,
      server_make: s.make || f.server_make,
      server_model: s.model || f.server_model,
    }));
  };

  // --- Venue handlers ---
  const openVenueDialog = (venue) => {
    if (venue === 'new') {
      setVenueForm({
        name: '', chain: '', city: '', state: '', country: 'US', zip_code: '', address: '',
        timezone: '', tech_contact_name: '', tech_contact_email: '', tech_contact_phone: '',
        booking_contact_name: '', booking_contact_email: '', notes: '',
        preferred_format: '', ingest_capability: '', responsiveness: '',
        ingest_software: '', kdm_method_preferred: '',
      });
      setVenueOutcomes(null);
      setVenuePastDeliveries(null);
    } else {
      setVenueForm({ ...venue, chain: venue.chain || '', city: venue.city || '', state: venue.state || '',
        address: venue.address || '', zip_code: venue.zip_code || '', timezone: venue.timezone || '',
        tech_contact_name: venue.tech_contact_name || '', tech_contact_email: venue.tech_contact_email || '',
        tech_contact_phone: venue.tech_contact_phone || '', booking_contact_name: venue.booking_contact_name || '',
        booking_contact_email: venue.booking_contact_email || '', notes: venue.notes || '',
        preferred_format: venue.preferred_format || '', ingest_capability: venue.ingest_capability || '',
        responsiveness: venue.responsiveness || '', ingest_software: venue.ingest_software || '',
        kdm_method_preferred: venue.kdm_method_preferred || '',
      });
      // Pull last-25 outcomes for the timeline.
      setVenueOutcomes(null);
      api.get(`/api/venues/${venue.id}/outcomes`).then(r => setVenueOutcomes(r.data || []))
        .catch(() => setVenueOutcomes([]));
      // Pull audit-log breadcrumbs for past KDM email sends. Surfaces
      // deliveries whose kdm_records have been hard-deleted with a
      // cancelled parent DCP.
      setVenuePastDeliveries(null);
      api.get(`/api/venues/${venue.id}/past-deliveries`).then(r => setVenuePastDeliveries(r.data || []))
        .catch(() => setVenuePastDeliveries([]));
    }
    setVenueDialog(venue);
  };

  const [fulfillRequestId, setFulfillRequestId] = useState(null);
  // Operator-complete-with-cert dialog state. Used when a venue replies
  // to the request via email (with .pem attached) instead of using the
  // public form. Operator uploads the cert here and the backend runs
  // the same archive+push pipeline the public form runs.
  const [completeReqDialog, setCompleteReqDialog] = useState(null);
  const [completeReqForm, setCompleteReqForm] = useState({
    venue_name: '', screen_name: 'Auditorium 1', chain: '', city: '', state: '',
    country: 'US', zip_code: '', street_address: '',
    tech_contact_name: '', tech_contact_email: '', tech_contact_phone: '',
    timezone: '', resolution_required: '', audio_version: '5.1', operator_note: '',
    cert_file: null,
  });
  const [completeReqBusy, setCompleteReqBusy] = useState(false);
  const [completeReqError, setCompleteReqError] = useState('');

  const openCompleteRequest = (r) => {
    const pf = r.prefill || {};
    setCompleteReqForm({
      venue_name: pf.venue_name || r.recipient_name || '',
      screen_name: (pf.screen_names && pf.screen_names[0]) || 'Auditorium 1',
      chain: pf.chain || '',
      city: pf.city || '',
      state: pf.state || '',
      country: pf.country || 'US',
      zip_code: pf.zip_code || '',
      street_address: pf.street_address || '',
      tech_contact_name: pf.tech_contact_name || r.recipient_name || '',
      tech_contact_email: pf.tech_contact_email || r.recipient_email || '',
      tech_contact_phone: pf.tech_contact_phone || '',
      timezone: pf.timezone || '',
      resolution_required: pf.resolution_required || '',
      audio_version: pf.audio_version || '5.1',
      operator_note: '',
      cert_file: null,
    });
    setCompleteReqError('');
    setCompleteReqDialog(r);
  };

  const submitCompleteRequest = async () => {
    setCompleteReqError('');
    if (!completeReqForm.venue_name.trim()) {
      setCompleteReqError('Venue name is required');
      return;
    }
    if (!completeReqForm.cert_file) {
      setCompleteReqError('Please attach the .pem certificate from the venue');
      return;
    }
    setCompleteReqBusy(true);
    try {
      const fd = new FormData();
      fd.append('certificate', completeReqForm.cert_file);
      Object.entries(completeReqForm).forEach(([k, v]) => {
        if (k === 'cert_file') return;
        if (v != null && v !== '') fd.append(k, v);
      });
      const { data } = await api.post(
        `/request/${completeReqDialog.token}/operator-complete`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setSnackbar({
        open: true,
        message: `Cert ingested for ${data.venue_name} — ${data.screen_name} (pushed to ${data.pushed_to_nodes} node${data.pushed_to_nodes === 1 ? '' : 's'})${data.push_error ? ` — push warning: ${data.push_error}` : ''}`,
        severity: data.push_error ? 'warning' : 'success',
      });
      setCompleteReqDialog(null);
      handleLoadLinks();
      dispatch(fetchVenues());
    } catch (e) {
      setCompleteReqError(e?.response?.data?.detail || e.message || 'Failed to complete request');
    } finally {
      setCompleteReqBusy(false);
    }
  };

  const handleSaveVenue = async () => {
    setVenueSaveError('');
    // Client-side validation: backend requires `name`. Without this
    // hint the dialog just sits silently when the user clicks Save
    // with the field blank — the unwrap() rejection lands nowhere.
    if (!venueForm.name || !venueForm.name.trim()) {
      setVenueSaveError('Venue name is required');
      return;
    }
    try {
      if (venueDialog === 'new') {
        const created = await dispatch(createVenue(venueForm)).unwrap();
        // Seed the initial primary tech contact into the new
        // venue_contacts table so the multi-contact UI shows it. Best-
        // effort — failure here doesn't roll back the venue create.
        if (created?.id && (venueForm.tech_contact_email || venueForm.tech_contact_name || venueForm.tech_contact_phone)) {
          try {
            await api.post(`/api/venues/${created.id}/contacts`, {
              role: 'tech_contact',
              name: venueForm.tech_contact_name || '',
              email: venueForm.tech_contact_email || '',
              phone: venueForm.tech_contact_phone || '',
              is_primary: true,
              position: 0,
            });
          } catch (_) { /* swallow — best-effort */ }
        }
        if (fulfillRequestId) {
          try {
            await api.patch(`/request/links/${fulfillRequestId}`, { used: true });
          } catch (_) { /* swallow — best-effort */ }
          setFulfillRequestId(null);
          handleLoadLinks();
        }
      } else {
        await dispatch(updateVenue({ venueId: venueDialog.id, venueData: venueForm })).unwrap();
      }
      setVenueDialog(null);
      dispatch(fetchVenues());
    } catch (e) {
      setVenueSaveError(typeof e === 'string' ? e : (e?.message || 'Failed to save venue'));
    }
  };

  const fulfillRequest = (r) => {
    const pf = r.prefill || {};
    setVenueForm({
      name: pf.venue_name || r.recipient_name || '',
      chain: pf.chain || '',
      city: pf.city || '',
      state: pf.state || '',
      country: pf.country || 'US',
      zip_code: pf.zip_code || '',
      address: pf.street_address || '',
      timezone: pf.timezone || '',
      tech_contact_name: pf.tech_contact_name || r.recipient_name || '',
      tech_contact_email: pf.tech_contact_email || r.recipient_email || '',
      tech_contact_phone: pf.tech_contact_phone || '',
      booking_contact_name: '',
      booking_contact_email: '',
      notes: `Manually fulfilled from venue request: ${r.title || ''}`.trim(),
    });
    setFulfillRequestId(r.id);
    setVenueDialog('new');
  };

  // --- Screen handlers ---
  const openScreenDialog = (venueId, screen) => {
    if (screen) {
      setScreenForm({ ...screen, screen_type: screen.screen_type || '',
        playback_type: screen.playback_type || 'dcp',
        projector_make: screen.projector_make || '',
        projector_model: screen.projector_model || '',
        projector_serial: screen.projector_serial || '',
        projector_resolution: screen.projector_resolution || '',
        max_audio_channels: screen.max_audio_channels || 8,
        server_make: screen.server_make || '', server_model: screen.server_model || '',
        server_serial: screen.server_serial || '',
        server_firmware_version: screen.server_firmware_version || '',
        audio_system_type: screen.audio_system_type || '',
        subtitle_capability: Array.isArray(screen.subtitle_capability) ? screen.subtitle_capability : [],
        aspect_ratios_supported: Array.isArray(screen.aspect_ratios_supported) ? screen.aspect_ratios_supported : [],
        three_d_system: screen.three_d_system || '',
        notes: screen.notes || '',
      });
    } else {
      setScreenForm({
        name: '', screen_type: '', playback_type: 'dcp',
        projector_make: '', projector_model: '',
        projector_serial: '', projector_resolution: '',
        is_3d: false, is_laser: false, is_hfr: false,
        max_audio_channels: 8, server_make: '', server_model: '', server_serial: '',
        server_firmware_version: '',
        audio_system_type: '', subtitle_capability: [], aspect_ratios_supported: [], three_d_system: '',
        notes: '',
      });
    }
    setScreenDialog({ venueId, screen });
  };

  const handleSaveScreen = async () => {
    if (screenDialog.screen) {
      await dispatch(updateScreen({ screenId: screenDialog.screen.id, screenData: screenForm })).unwrap();
    } else {
      await dispatch(createScreen({ venueId: screenDialog.venueId, screenData: screenForm })).unwrap();
    }
    setScreenDialog(null);
    dispatch(fetchVenues());
  };

  const handleUploadCert = async () => {
    if (!certUpload || !certFile) return;
    try {
      await dispatch(uploadScreenCertificate({ screenId: certUpload.id, file: certFile })).unwrap();
      setSnackbar({ open: true, message: `Certificate updated for ${certUpload.name} and pushed to all DCP nodes`, severity: 'success' });
      setCertUpload(null);
      setCertFile(null);
      dispatch(fetchVenues());
    } catch (err) {
      setSnackbar({ open: true, message: typeof err === 'string' ? err : 'Certificate upload failed', severity: 'error' });
    }
  };

  const openInviteForVenue = (venue) => {
    setRequestForm({
      title: '',
      recipient_name: venue.tech_contact_name || '',
      recipient_email: venue.tech_contact_email || '',
      expires_days: 14,
      first_screening_at: '',
      last_screening_at: '',
      send_email: !!venue.tech_contact_email,
      // Pre-fill from the existing venue so the theater can fact-check.
      // screen_names is intentionally left empty — they're being asked to
      // add a NEW auditorium.
      chain: venue.chain || '',
      venue_name: venue.name || '',
      screen_names: [''],
      server_info: '',
      projector_info: '',
      server_serial: '',
      street_address: venue.address || '',
      city: venue.city || '',
      state: venue.state || '',
      country: venue.country || 'US',
      zip_code: venue.zip_code || '',
      tech_contact_phone: venue.tech_contact_phone || '',
      timezone: venue.timezone || '',
      version: '',
      resolution_required: '',
    });
    setRequestLinkResult(null);
    setRequestLinkDialog(true);
  };

  const handleCreateRequestLink = async () => {
    try {
      // Build prefill from any non-empty optional fields
      const prefillKeys = [
        'chain', 'venue_name', 'server_info', 'projector_info',
        'server_serial', 'street_address', 'city', 'state', 'country', 'zip_code',
        'tech_contact_phone', 'timezone', 'version', 'resolution_required',
      ];
      const prefill = {};
      prefillKeys.forEach(k => {
        if (requestForm[k]) prefill[k] = requestForm[k];
      });
      // Recipient name/email also seed the contact fields
      if (requestForm.recipient_name) prefill.tech_contact_name = requestForm.recipient_name;
      if (requestForm.recipient_email) prefill.tech_contact_email = requestForm.recipient_email;
      // Multiple auditorium pre-fills go in as a list
      const cleanScreens = (requestForm.screen_names || []).map(s => s.trim()).filter(Boolean);
      if (cleanScreens.length) prefill.screen_names = cleanScreens;

      const body = {
        title: requestForm.title || '',
        recipient_name: requestForm.recipient_name || '',
        recipient_email: requestForm.recipient_email || '',
        cc_email: requestForm.cc_email || '',
        expires_days: requestForm.expires_days || 14,
        send_email: requestForm.send_email || false,
        message: requestForm.message || '',
        first_screening_at: requestForm.first_screening_at
          ? new Date(requestForm.first_screening_at).toISOString() : null,
        last_screening_at: requestForm.last_screening_at
          ? new Date(requestForm.last_screening_at).toISOString() : null,
        prefill: Object.keys(prefill).length ? prefill : null,
      };
      const { data } = await api.post('/request/create-link', body);
      setRequestLinkResult(data);
      handleLoadLinks();
    } catch (err) {
      setRequestLinkResult({ error: err.response?.data?.detail || 'Failed to create link' });
    }
  };

  const handleLoadLinks = async () => {
    try {
      const { data } = await api.get('/request/links');
      setRequestLinks(data);
    } catch (_err) { /* ignore */ }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Distinct chain names for the Add Venue dropdown
  const existingChains = Array.from(
    new Set((venues || []).map(v => v.chain).filter(Boolean))
  ).sort();

  // Distinct states for filter dropdown.
  const existingStates = Array.from(
    new Set((venues || []).map(v => (v.state || '').trim().toUpperCase()).filter(Boolean))
  ).sort();

  // Filter + sort the venue list before grouping. All client-side — fast
  // for hundreds of venues, no API surface change.
  const q = (searchQuery || '').trim().toLowerCase();
  const filteredVenues = (venues || []).filter(v => {
    if (q) {
      const hay = [
        v.name, v.city, v.state, v.chain,
        v.tech_contact_name, v.tech_contact_email, v.tech_contact_phone,
        v.booking_contact_name, v.booking_contact_email,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (stateFilter !== 'all' && (v.state || '').trim().toUpperCase() !== stateFilter) return false;
    if (chainFilter !== 'all') {
      if (chainFilter === '__independent__' && v.chain) return false;
      if (chainFilter !== '__independent__' && v.chain !== chainFilter) return false;
    }
    if (certFilter !== 'all') {
      const screensWithCert = (v.screens || []).filter(s => s.certificate_filename).length;
      if (certFilter === 'with' && screensWithCert === 0) return false;
      if (certFilter === 'without' && screensWithCert > 0) return false;
      if (certFilter === 'partial') {
        const total = (v.screens || []).length;
        if (total === 0 || screensWithCert === 0 || screensWithCert === total) return false;
      }
    }
    return true;
  });

  // Group filtered venues using the active sort/grouping mode.
  const groups = {};  // groupKey → venue[]
  const groupOrder = [];
  const _push = (k, v) => {
    if (!groups[k]) { groups[k] = []; groupOrder.push(k); }
    groups[k].push(v);
  };
  if (sortBy === 'state') {
    filteredVenues
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .forEach(v => _push((v.state || 'No State').toUpperCase().trim() || 'No State', v));
    groupOrder.sort();
  } else if (sortBy === 'name') {
    filteredVenues
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .forEach(v => _push('All Venues', v));
  } else {
    // chain (default)
    filteredVenues.forEach(v => _push(v.chain || 'Independent Venues', v));
    // Sort group keys: real chains alpha, "Independent Venues" last.
    groupOrder.sort((a, b) => {
      if (a === 'Independent Venues') return 1;
      if (b === 'Independent Venues') return -1;
      return a.localeCompare(b);
    });
    // Sort venues within each group alpha by name.
    Object.keys(groups).forEach(k => groups[k].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
  }
  const filterActive = q || stateFilter !== 'all' || chainFilter !== 'all' || certFilter !== 'all';

  return (
    <Box>
      <PipelineProgress />
      <PageHeader
        title="Venues"
        subtitle="Theater catalog, screens, and projector certificates"
        actions={
          <>
            <FormControlLabel
              control={<Switch size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
              label="Show archived"
              sx={{ mr: 1, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
            />
            <Tooltip title="Refresh"><IconButton onClick={() => dispatch(fetchVenues({ includeArchived: showArchived }))}><RefreshIcon /></IconButton></Tooltip>
            <Tooltip title="Email the venue a self-serve form they fill in (contact info + cert upload). Use when you don't have all the details yet.">
              <Button
                variant="outlined" startIcon={<SendIcon />}
                aria-label="Ask the venue"
                onClick={() => { setRequestLinkDialog(true); setRequestLinkResult(null); }}
              >
                Ask the venue
              </Button>
            </Tooltip>
            <Tooltip title="Enter contact, screens, and (optionally) certs yourself. Use when you already have everything from the venue.">
              <Button
                variant="contained" startIcon={<AddIcon />}
                aria-label="Add venue manually"
                onClick={() => openVenueDialog('new')}
              >
                Add manually
              </Button>
            </Tooltip>
          </>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* v11.0.0 — cert urgency banner. The single most workflow-
          blocking problem in the entire pipeline (per operator
          audit): a missing cert means no KDM, which means no
          encrypted delivery. Pre-fix it was a small grey chip in the
          stats strip; now it's the first thing the operator sees on
          this page. Click-through filters the list to the affected
          venues, so there's a clear path to fix. */}
      {(() => {
        // v11.0.0 — exempt screens flagged cert_not_required from the
        // missing-cert count. The banner counts only DCP-typed,
        // active, non-exempt screens that genuinely need a cert.
        const needsCert = (s) =>
          s
          && (s.playback_type || 'dcp') !== 'mp4'
          && !s.cert_not_required;
        const screensNeedingCert = venues.flatMap(
          (v) => (v.screens || []).filter(needsCert),
        );
        const missingCerts = screensNeedingCert.filter((s) => !s.certificate_filename).length;
        if (missingCerts === 0) return null;
        return (
          <Alert
            severity="warning"
            data-testid="cert-urgency-banner"
            sx={{ mb: 2 }}
            action={
              <Button
                color="warning"
                size="small"
                variant="contained"
                onClick={() => setCertFilter('without')}
              >
                Show {missingCerts === 1 ? 'it' : 'them'}
              </Button>
            }
          >
            <strong>{missingCerts} screen{missingCerts === 1 ? '' : 's'} {missingCerts === 1 ? 'is' : 'are'} missing a projector certificate.</strong>{' '}
            Without a cert on a screen, no KDM can be generated for it — distribution to that screen will fail. Fix these before your next delivery.
          </Alert>
        );
      })()}

      {/* Compact stats strip — three pills, total ~32px tall vs the
          old card grid that ate ~120px. Filter chips below echo
          "missing certs" → click to filter to those venues. */}
      {(() => {
        const totalScreens = venues.reduce((sum, v) => sum + (v.screens?.length || 0), 0);
        const totalCerts = venues.reduce((sum, v) => sum + (v.screens?.filter(s => s.certificate_filename)?.length || 0), 0);
        const missingCerts = totalScreens - totalCerts;
        return (
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="h6" fontWeight={700} color="primary">{venues.length}</Typography>
              <Typography variant="caption" color="text.secondary">venues</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="h6" fontWeight={700} color="info.main">{totalScreens}</Typography>
              <Typography variant="caption" color="text.secondary">screens</Typography>
            </Box>
            <Tooltip title={missingCerts > 0 ? `Click to filter to venues missing certs (${missingCerts})` : 'All screens have certificates'}>
              <Box
                sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, cursor: missingCerts > 0 ? 'pointer' : 'default' }}
                onClick={() => missingCerts > 0 && setCertFilter(certFilter === 'without' ? 'all' : 'without')}
              >
                <Typography variant="h6" fontWeight={700} color="success.main">{totalCerts}</Typography>
                <Typography variant="caption" color="text.secondary">certs</Typography>
                {missingCerts > 0 && (
                  <Chip
                    size="small"
                    label={`${missingCerts} missing`}
                    color={certFilter === 'without' ? 'warning' : 'default'}
                    variant={certFilter === 'without' ? 'filled' : 'outlined'}
                    sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            </Tooltip>
          </Box>
        );
      })()}

      {/* Filter / sort bar */}
      {(venues || []).length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder={
              filteredVenues.length === venues.length
                ? `Search ${venues.length} venue${venues.length === 1 ? '' : 's'} — name, city, state, contact…`
                : `${filteredVenues.length} of ${venues.length} match`
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: '1 1 280px', minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>State</InputLabel>
            <Select label="State" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              {existingStates.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Chain</InputLabel>
            <Select label="Chain" value={chainFilter} onChange={(e) => setChainFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              {existingChains.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              <MenuItem value="__independent__">Independent</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Certs</InputLabel>
            <Select label="Certs" value={certFilter} onChange={(e) => setCertFilter(e.target.value)}>
              <MenuItem value="all">Any</MenuItem>
              <MenuItem value="with">With cert</MenuItem>
              <MenuItem value="without">Missing</MenuItem>
              <MenuItem value="partial">Partial</MenuItem>
            </Select>
          </FormControl>
          {filterActive && (
            <Button size="small" onClick={() => {
              setSearchQuery(''); setStateFilter('all'); setChainFilter('all'); setCertFilter('all');
            }}>Clear filters</Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title={`Group venues by ${sortBy === 'chain' ? 'chain (current)' : sortBy === 'state' ? 'state' : 'name (flat list)'}`}>
            <IconButton
              size="small"
              onClick={(e) => setOptionsAnchor(e.currentTarget)}
              sx={{ color: 'text.secondary' }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={optionsAnchor}
            open={Boolean(optionsAnchor)}
            onClose={() => setOptionsAnchor(null)}
          >
            <MenuItem disabled sx={{ opacity: '0.6 !important', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Group by
            </MenuItem>
            {[
              { v: 'chain', label: 'Chain' },
              { v: 'state', label: 'State' },
              { v: 'name', label: 'Name (flat)' },
              { v: 'table', label: 'Table (sortable, bulk-select)' },
            ].map(opt => (
              <MenuItem
                key={opt.v}
                selected={sortBy === opt.v}
                onClick={() => { setSortBy(opt.v); setOptionsAnchor(null); }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Menu>
        </Paper>
      )}

      {/* Outstanding venue requests table moved to /distribution in
          v10.19.51. The Complete-with-cert and Fulfill-manually flows
          still live on this page; the table over there sets the
          ?complete=<id> / ?fulfill=<id> query param and the useEffect
          below picks it up to open the matching dialog. */}

      {/* Bulk action bar — surfaces when rows are selected in table mode.
          Lives above the grid so it doesn't shift content as selection
          changes. */}
      {sortBy === 'table' && tableSelection.length > 0 && (
        <Paper
          sx={{
            p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2,
            borderLeft: 4, borderColor: 'primary.main',
            bgcolor: 'action.selected',
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {tableSelection.length} venue{tableSelection.length === 1 ? '' : 's'} selected
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" onClick={() => setTableSelection([])}>Clear</Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setBulkRequestDialog(true)}
          >
            Send request form to {tableSelection.length}
          </Button>
        </Paper>
      )}

      {/* Table mode: virtualized DataGrid with sortable columns + bulk
          select. Skip the grouped-card rendering entirely in this mode. */}
      {sortBy === 'table' ? (
        <VenuesTableView
          venues={filteredVenues}
          selectionModel={tableSelection}
          onSelectionChange={(model) => {
            // MUI x-data-grid v8 returns either an array or a Set-like
            // object depending on rowSelectionPropagation; normalize.
            if (Array.isArray(model)) setTableSelection(model);
            else if (model?.ids) setTableSelection(Array.from(model.ids));
            else setTableSelection(Array.from(model || []));
          }}
          onEdit={(venue) => openVenueDialog('edit', venue)}
          onSendRequest={(venue) => {
            // Use the existing single-venue request flow for the per-row
            // action; consistent with the page header's button.
            setRequestForm(f => ({
              ...f,
              chain: venue.chain || '',
              venue_name: venue.name || '',
              recipient_name: venue.tech_contact_name || '',
              recipient_email: venue.tech_contact_email || '',
              tech_contact_phone: venue.tech_contact_phone || '',
              city: venue.city || '',
              state: venue.state || '',
              country: venue.country || 'US',
              zip_code: venue.zip_code || '',
              street_address: venue.address || '',
              timezone: venue.timezone || '',
            }));
            setRequestLinkResult(null);
            setRequestLinkDialog(true);
          }}
        />
      ) : groupOrder.map(groupKey => {
        const groupVenues = groups[groupKey] || [];
        // Default to collapsed so the operator sees a tight chain/state
        // Auto-expand when filters are active, OR when the catalog is
        // small (≤15 venues across all groups) — clicking through every
        // group to see 5 theaters is high-friction. Larger catalogs
        // collapse by default so the page scans cleanly.
        const totalVenues = filteredVenues.length;
        const expanded = !!filterActive || totalVenues <= 15;
        // Roll up screen + cert counts for this group so the header
        // header gives at-a-glance info instead of the redundant
        // "1 venue" badge.
        const groupScreens = groupVenues.reduce((n, v) => n + (v.screens?.filter(s => s.is_active).length || 0), 0);
        const groupCerts = groupVenues.reduce((n, v) => n + (v.screens?.filter(s => s.is_active && s.certificate_filename).length || 0), 0);
        // Key includes sortBy so flipping Group-by remounts each Accordion
        // and re-applies the default.
        return (
          <Accordion key={`${sortBy}-${groupKey}`} defaultExpanded={expanded} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 22 }} />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" fontWeight={600}>{groupKey}</Typography>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  <Chip
                    size="small"
                    label={`${groupVenues.length} venue${groupVenues.length === 1 ? '' : 's'}`}
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  <Chip
                    size="small"
                    label={`${groupScreens} screen${groupScreens === 1 ? '' : 's'}`}
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  {groupScreens > 0 && (
                    <Chip
                      size="small"
                      label={groupCerts === groupScreens
                        ? `${groupCerts} certs`
                        : `${groupCerts}/${groupScreens} certs`}
                      color={groupCerts === groupScreens ? 'success' : groupCerts === 0 ? 'warning' : 'default'}
                      variant={groupCerts === groupScreens ? 'outlined' : 'filled'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {groupVenues.map(venue => (
                <VenueCard
                  key={venue.id}
                  venue={venue}
                  defaultExpanded={!!filterActive}
                  onEdit={() => openVenueDialog(venue)}
                  onRecordOutcome={() => setVenueOutcomeDialog({ venueId: venue.id, label: venue.name })}
                  onAddScreen={() => openScreenDialog(venue.id)}
                  onInviteToAddScreen={() => openInviteForVenue(venue)}
                  onEditScreen={(s) => openScreenDialog(venue.id, s)}
                  onUploadCert={(s) => setCertUpload(s)}
                  onDeleteScreen={async (s) => {
                    if (!window.confirm(`Delete screen "${s.name}" from ${venue.name}?\n\nThis is a soft-delete: KDM history that references this screen is preserved, the screen just stops appearing in the active catalog.`)) return;
                    try {
                      await api.delete(`/api/venues/screens/${s.id}`);
                      dispatch(fetchVenues({ includeArchived: showArchived }));
                    } catch (e) {
                      alert(`Delete failed: ${e?.response?.data?.detail || e.message || 'unknown error'}`);
                    }
                  }}
                  onDelete={() => setDeleteConfirm(venue)}
                  onRestore={async () => {
                    try {
                      await dispatch(restoreVenue(venue.id)).unwrap();
                      dispatch(fetchVenues({ includeArchived: showArchived }));
                    } catch (_) { /* swallow — best-effort */ }
                  }}
                  onPurge={async () => {
                    const ok1 = window.confirm(
                      `Permanently delete "${venue.name}"?\n\n` +
                      `This cannot be undone. The venue + all its screens are removed from the database. ` +
                      `Bookings and KDM history that referenced this venue stay (they NULL out the FK).`,
                    );
                    if (!ok1) return;
                    const typed = window.prompt(`Type DELETE to confirm permanent removal of ${venue.name}:`);
                    if (typed !== 'DELETE') return;
                    try {
                      await dispatch(purgeVenue(venue.id)).unwrap();
                      dispatch(fetchVenues({ includeArchived: showArchived }));
                    } catch (e) {
                      alert(`Purge failed: ${typeof e === 'string' ? e : (e?.message || 'unknown error')}`);
                    }
                  }}
                />
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {filteredVenues.length === 0 && venues.length > 0 && filterActive && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No venues match the current filters.
          </Typography>
          <Button size="small" sx={{ mt: 1 }} onClick={() => {
            setSearchQuery(''); setStateFilter('all'); setChainFilter('all'); setCertFilter('all');
          }}>Clear filters</Button>
        </Paper>
      )}

      {venues.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>No venues in catalog. Add your first venue to get started.</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openVenueDialog('new')}>
            Add Venue
          </Button>
        </Paper>
      )}

      {/* Venue dialog */}
      <Dialog open={Boolean(venueDialog)} onClose={() => { setVenueDialog(null); setVenueSaveError(''); }} maxWidth="md" fullWidth>
        <DialogTitle>{venueDialog === 'new' ? 'Add Venue' : 'Edit Venue'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {venueSaveError && (
              <Alert severity="error" onClose={() => setVenueSaveError('')}>{venueSaveError}</Alert>
            )}
            <Grid container spacing={2}>
              <Grid size={8}><TextField label="Venue Name" fullWidth required value={venueForm.name} onChange={(e) => setVenueForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Lincoln Square" /></Grid>
              <Grid size={4}>
                <Autocomplete
                  freeSolo
                  options={existingChains}
                  value={venueForm.chain || ''}
                  onChange={(e, value) => setVenueForm(f => ({ ...f, chain: value || '' }))}
                  onInputChange={(e, value) => setVenueForm(f => ({ ...f, chain: value || '' }))}
                  renderInput={(params) => (
                    <TextField {...params} label="Chain" placeholder="e.g. AMC (or type a new chain)" />
                  )}
                />
              </Grid>
            </Grid>
            <TextField label="Address" fullWidth value={venueForm.address} onChange={(e) => setVenueForm(f => ({ ...f, address: e.target.value }))} placeholder="e.g. 1800 Vine Street, Suite 334" />
            <Grid container spacing={2}>
              <Grid size={3}><TextField label="City" fullWidth value={venueForm.city} onChange={(e) => setVenueForm(f => ({ ...f, city: e.target.value }))} /></Grid>
              <Grid size={3}><TextField label="State" fullWidth value={venueForm.state} onChange={(e) => {
                const st = e.target.value;
                setVenueForm(f => {
                  const updated = { ...f, state: st };
                  const tz = guessTimezone(st, f.country);
                  if (tz && (!f.timezone || f.timezone === guessTimezone(f.state, f.country))) updated.timezone = tz;
                  return updated;
                });
              }} /></Grid>
              <Grid size={2}><TextField label="Zip" fullWidth value={venueForm.zip_code || ''} onChange={(e) => setVenueForm(f => ({ ...f, zip_code: e.target.value }))} /></Grid>
              <Grid size={4}><TextField label="Country" fullWidth value={venueForm.country} onChange={(e) => {
                const co = e.target.value;
                setVenueForm(f => {
                  const updated = { ...f, country: co };
                  const tz = guessTimezone(f.state, co);
                  if (tz && (!f.timezone || f.timezone === guessTimezone(f.state, f.country))) updated.timezone = tz;
                  return updated;
                });
              }} /></Grid>
            </Grid>
            <Autocomplete
              freeSolo
              options={ALL_TIMEZONES}
              value={venueForm.timezone || null}
              onChange={(e, value) => setVenueForm(f => ({ ...f, timezone: value || '' }))}
              onInputChange={(e, value, reason) => { if (reason === 'input') setVenueForm(f => ({ ...f, timezone: value || '' })); }}
              renderInput={(params) => (
                <TextField {...params} label="Timezone" placeholder="America/New_York" helperText="Auto-filled from state/country. Override if needed." />
              )}
            />
            <Divider />
            {venueDialog === 'new' ? (
              <>
                <Typography variant="subtitle2">Initial Contact</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                  Seed one contact now (primary tech contact). After the venue is created you can add as many more contacts as you want — projectionists, managers, owner, etc. — from the Contacts section on the venue card.
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={4}><TextField label="Name" fullWidth value={venueForm.tech_contact_name} onChange={(e) => setVenueForm(f => ({ ...f, tech_contact_name: e.target.value }))} /></Grid>
                  <Grid size={4}><TextField label="Email" fullWidth value={venueForm.tech_contact_email} onChange={(e) => setVenueForm(f => ({ ...f, tech_contact_email: e.target.value }))} /></Grid>
                  <Grid size={4}><TextField label="Phone" fullWidth value={venueForm.tech_contact_phone} onChange={(e) => setVenueForm(f => ({ ...f, tech_contact_phone: e.target.value }))} /></Grid>
                </Grid>
              </>
            ) : (
              <VenueContactsEditor venueId={venueDialog?.id} />
            )}
            {/* Delivery profile — observation-driven knowledge about
                this venue. Surfaced in DeliverDialog so the next
                operator picks the right format / KDM-delivery method
                without re-discovering the venue's quirks. */}
            <Box sx={{ mt: 1, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem', fontWeight: 600 }}>
                Delivery profile
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary', fontSize: '0.72rem' }}>
                What we've learned about how this venue likes to receive content. Set after observing one or two deliveries; leave blank if unknown.
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TextField select fullWidth size="small" label="Preferred archive format"
                    value={venueForm.preferred_format || ''}
                    onChange={(e) => setVenueForm(f => ({ ...f, preferred_format: e.target.value }))}
                    helperText="Operator override; otherwise auto-derived from delivery history">
                    <MenuItem value=""><em>(unknown — defer to history)</em></MenuItem>
                    <MenuItem value="zip">.zip (universal, recommended default)</MenuItem>
                    <MenuItem value="tar">.tar (cinema-IT-equipped)</MenuItem>
                    <MenuItem value="folder">folder (live filesystem)</MenuItem>
                    <MenuItem value="drive">hard drive (overnight)</MenuItem>
                    <MenuItem value="quicktime">QuickTime / MP4</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={6}>
                  <TextField select fullWidth size="small" label="Ingest capability"
                    value={venueForm.ingest_capability || ''}
                    onChange={(e) => setVenueForm(f => ({ ...f, ingest_capability: e.target.value }))}>
                    <MenuItem value=""><em>(unknown)</em></MenuItem>
                    <MenuItem value="pro">Pro IT</MenuItem>
                    <MenuItem value="standard">Standard projectionist</MenuItem>
                    <MenuItem value="basic">Basic — engages with guidance</MenuItem>
                    <MenuItem value="needs_handholding">Hand-hold — walk through steps</MenuItem>
                    <MenuItem value="low_engagement">Low engagement — won't troubleshoot</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={6}>
                  <TextField select fullWidth size="small" label="Responsiveness"
                    value={venueForm.responsiveness || ''}
                    onChange={(e) => setVenueForm(f => ({ ...f, responsiveness: e.target.value }))}>
                    <MenuItem value=""><em>(unknown)</em></MenuItem>
                    <MenuItem value="quick">Quick (same day)</MenuItem>
                    <MenuItem value="slow">Slow (1–2 days)</MenuItem>
                    <MenuItem value="unresponsive">Unresponsive (pre-call)</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={12}>
                  <TextField select fullWidth size="small" label="KDM delivery preference"
                    value={venueForm.kdm_method_preferred || ''}
                    onChange={(e) => setVenueForm(f => ({ ...f, kdm_method_preferred: e.target.value }))}>
                    <MenuItem value=""><em>(unknown)</em></MenuItem>
                    <MenuItem value="link">Secure download link (default)</MenuItem>
                    <MenuItem value="inline_xml">Inline .xml attachment</MenuItem>
                    <MenuItem value="zip_attach">.zip-wrapped attachment</MenuItem>
                    <MenuItem value="dropbox">Dropbox / WeTransfer</MenuItem>
                    <MenuItem value="hand">In-person / USB hand-off</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Box>

            {/* Outcomes timeline — append-only history of prior delivery
                attempts to this venue. Read-only here; outcomes are
                recorded from the DCP Distribution per-recipient card. */}
            {venueDialog !== 'new' && (
              <Box sx={{ mt: 1, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem', fontWeight: 600 }}>
                  Delivery history
                </Typography>
                {venueOutcomes === null && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Loading…</Typography>
                )}
                {venueOutcomes !== null && venueOutcomes.length === 0
                  && venuePastDeliveries !== null && venuePastDeliveries.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No outcomes recorded yet. After a delivery, hit the "Record outcome" button on the per-recipient card in Distribution.
                  </Typography>
                )}
                {venueOutcomes !== null && venueOutcomes.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {venueOutcomes.map(o => (
                      <Typography key={o.id} variant="caption" sx={{ fontSize: '0.75rem' }}>
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {new Date(o.created_at).toLocaleDateString()} {new Date(o.created_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                        </Box>
                        {' · '}
                        <Box component="span" sx={{ fontWeight: 500 }}>.{o.format_used || '?'}</Box>
                        {' → '}
                        <Box component="span" sx={{
                          color: ['test_successful','screening_successful','success_clean','success_with_help'].includes(o.outcome) ? 'success.main'
                            : ['format_unknown','download_failed','kdm_filter_blocked','av_issue'].includes(o.outcome) ? 'error.main'
                            : 'warning.main',
                        }}>{o.outcome.replace(/_/g, ' ')}</Box>
                        {o.recorded_by_username && (
                          <Box component="span" sx={{ color: 'text.secondary' }}> · by {o.recorded_by_username}</Box>
                        )}
                        {o.notes && (
                          <Box component="div" sx={{ pl: 2, color: 'text.secondary', fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                            {o.notes}
                          </Box>
                        )}
                      </Typography>
                    ))}
                  </Box>
                )}
                {/* v10.18.26: KDM email events reconstructed from the
                    audit log. Surfaces past sends whose kdm_records
                    rows have been hard-deleted along with a cancelled
                    parent DCP. Past = success per operator direction
                    (the show played; cert validity at send time is
                    irrelevant to the historical record). */}
                {venuePastDeliveries !== null && venuePastDeliveries.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: venueOutcomes && venueOutcomes.length > 0 ? 1.5 : 0 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontStyle: 'italic' }}>
                      Past KDM sends (from audit log)
                    </Typography>
                    {venuePastDeliveries.map(pd => (
                      <Typography key={`pd-${pd.id}`} variant="caption" sx={{ fontSize: '0.75rem' }}>
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {new Date(pd.sent_at).toLocaleDateString()} {new Date(pd.sent_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                        </Box>
                        {' · '}
                        <Box component="span" sx={{ fontWeight: 500 }}>KDM emailed</Box>
                        {' → '}
                        <Box component="span" sx={{ color: 'success.main' }}>success</Box>
                        <Box component="span" sx={{ color: 'text.secondary' }}> · {pd.sent_to}</Box>
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <TextField label="Notes" fullWidth multiline minRows={2} maxRows={10} value={venueForm.notes} onChange={(e) => setVenueForm(f => ({ ...f, notes: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { height: 'auto !important' } }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVenueDialog(null)}>Cancel</Button>
          {venueDialog !== 'new' && venueDialog?.id && (
            <>
              <Button variant="outlined" size="medium"
                onClick={() => setVenueOutcomeDialog({ venueId: venueDialog.id, label: venueDialog.name })}>
                Record outcome
              </Button>
              <Button variant="outlined" size="medium"
                onClick={() => setVenueComposer({ venueId: venueDialog.id, label: venueDialog.name })}>
                Compose email
              </Button>
            </>
          )}
          {venueDialog === 'new' && (
            <Button variant="outlined" startIcon={<SendIcon />} onClick={() => {
              setRequestLinkDialog(true);
              setRequestForm(f => ({ ...f, title: venueForm.name, recipient_name: venueForm.tech_contact_name, recipient_email: venueForm.tech_contact_email }));
              setRequestLinkResult(null);
            }} disabled={!venueForm.tech_contact_email}>
              Send Invite Instead
            </Button>
          )}
          <Button variant="contained" onClick={handleSaveVenue} disabled={!venueForm.name}>{venueDialog === 'new' ? 'Add Venue' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Screen dialog */}
      <Dialog open={Boolean(screenDialog)} onClose={() => setScreenDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {screenDialog?.screen ? 'Edit Projector / Screen' : 'Add Projector / Screen'}
        </DialogTitle>
        <DialogContent>
          {/* v11.0.0 — name + intent. The operator audit called out
              that "Screen" reads as "an auditorium number" to a
              first-time user, when in our model a screen is the
              projector-server + cert bundle for one auditorium. */}
          {!screenDialog?.screen && (
            <Alert severity="info" sx={{ mb: 2 }} icon={false}>
              A screen is <strong>one auditorium&rsquo;s projection server</strong> (its make + model + serial) plus the certificate
              we use to generate KDMs for it. Add one screen per server in the venue.
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={4}><TextField label="Screen Name" fullWidth required value={screenForm.name} onChange={(e) => setScreenForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Screen 1, IMAX, Comedy Room" /></Grid>
              <Grid size={4}>
                <FormControl fullWidth>
                  <InputLabel>Playback Type</InputLabel>
                  <Select
                    value={screenForm.playback_type || 'dcp'}
                    label="Playback Type"
                    onChange={(e) => setScreenForm(f => ({ ...f, playback_type: e.target.value }))}
                  >
                    <MenuItem value="dcp">DCP (cinema projector)</MenuItem>
                    <MenuItem value="mp4">MP4 (TV / generic)</MenuItem>
                    <MenuItem value="both">Both</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={4}>
                <FormControl fullWidth>
                  <InputLabel>Screen Type</InputLabel>
                  <Select value={screenForm.screen_type} label="Screen Type" onChange={(e) => setScreenForm(f => ({ ...f, screen_type: e.target.value }))}>
                    {SCREEN_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {screenForm.playback_type === 'mp4' && (
              <Alert severity="info" sx={{ py: 0 }}>
                MP4-only screen — projector / server / certificate sections aren't required. Delivery is a watermarked MP4 share to the venue's contact email.
              </Alert>
            )}
            {/* v11.0.0 — cert-not-required flag for unencrypted-only
                venues + archival / backup destinations. */}
            {screenForm.playback_type !== 'mp4' && (
              <Box sx={{ mt: 1, mb: 1, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!screenForm.cert_not_required}
                      onChange={(e) => setScreenForm(f => ({ ...f, cert_not_required: e.target.checked }))}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        This screen doesn&rsquo;t need a projector certificate
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Use for unencrypted-only houses (e.g. Flappers) or archival / backup destinations
                        (e.g. Filmmaker Master Backup). When set, this screen won&rsquo;t appear in the
                        cert-urgency banner or count toward &ldquo;missing certs.&rdquo; You also won&rsquo;t
                        be able to distribute an encrypted DCP to it.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}
            <Typography variant="subtitle2">Projector</Typography>
            <Grid container spacing={2}>
              <Grid size={8}>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={projectorCatalog}
                  getOptionLabel={(o) => typeof o === 'string' ? o : projectorLabel(o)}
                  filterOptions={(opts, { inputValue }) => filterEquipment(opts, inputValue)}
                  inputValue={`${screenForm.projector_make || ''} ${screenForm.projector_model || ''}`.trim()}
                  onInputChange={(e, value, reason) => {
                    if (reason === 'input') {
                      // Split free-typed input into make + model on first space
                      const parts = (value || '').trim().split(/\s+/);
                      setScreenForm(f => ({
                        ...f,
                        projector_make: parts[0] || '',
                        projector_model: parts.slice(1).join(' ') || '',
                      }));
                    }
                  }}
                  onChange={(e, picked) => {
                    if (picked && typeof picked === 'object') applyProjectorPick(picked);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Make + Model" placeholder="Start typing — e.g. Barco SP4K, Christie 4325, NC1402…"
                      helperText="Pick from the list to auto-fill resolution + capabilities, or type a custom entry." />
                  )}
                />
              </Grid>
              <Grid size={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Resolution</InputLabel>
                  <Select value={screenForm.projector_resolution || ''} label="Resolution" onChange={(e) => setScreenForm(f => ({ ...f, projector_resolution: e.target.value }))}>
                    <MenuItem value="2K">2K</MenuItem>
                    <MenuItem value="4K">4K</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              label="Projector Serial #" fullWidth size="small"
              value={screenForm.projector_serial || ''}
              onChange={(e) => setScreenForm(f => ({ ...f, projector_serial: e.target.value }))}
              placeholder="e.g. 510949005"
              helperText="Stamped on the projector lamp-house plate. Useful for warranty / firmware lookups."
            />
            <Grid container spacing={2}>
              <Grid size={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Audio</InputLabel>
                  <Select value={screenForm.max_audio_channels || 8} label="Audio" onChange={(e) => setScreenForm(f => ({ ...f, max_audio_channels: parseInt(e.target.value) }))}>
                    <MenuItem value={2}>Stereo (2ch)</MenuItem>
                    <MenuItem value={6}>5.1 Surround (6ch)</MenuItem>
                    <MenuItem value={8}>7.1 Surround (8ch)</MenuItem>
                    <MenuItem value={16}>Dolby Atmos (16ch+)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={8}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%' }}>
                  <FormControlLabel control={<Checkbox checked={screenForm.is_3d} onChange={(e) => setScreenForm(f => ({ ...f, is_3d: e.target.checked }))} size="small" />} label="3D" />
                  <FormControlLabel control={<Checkbox checked={screenForm.is_laser} onChange={(e) => setScreenForm(f => ({ ...f, is_laser: e.target.checked }))} size="small" />} label="Laser" />
                  <FormControlLabel control={<Checkbox checked={screenForm.is_hfr} onChange={(e) => setScreenForm(f => ({ ...f, is_hfr: e.target.checked }))} size="small" />} label="HFR" />
                </Box>
              </Grid>
            </Grid>
            <Typography variant="subtitle2">Media Server / IMB</Typography>
            <Autocomplete
              freeSolo
              size="small"
              options={serverCatalog}
              getOptionLabel={(o) => typeof o === 'string' ? o : serverLabel(o)}
              filterOptions={(opts, { inputValue }) => filterEquipment(opts, inputValue)}
              inputValue={`${screenForm.server_make || ''} ${screenForm.server_model || ''}`.trim()}
              onInputChange={(e, value, reason) => {
                if (reason === 'input') {
                  const parts = (value || '').trim().split(/\s+/);
                  setScreenForm(f => ({
                    ...f,
                    server_make: parts[0] || '',
                    server_model: parts.slice(1).join(' ') || '',
                  }));
                }
              }}
              onChange={(e, picked) => {
                if (picked && typeof picked === 'object') applyServerPick(picked);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Make + Model" placeholder="Start typing — e.g. Dolby IMS3000, GDC SR-1000, Christie ICMP…"
                  helperText="Pick from the list or type a custom entry." />
              )}
            />
            <TextField label="Media Server Serial #" fullWidth value={screenForm.server_serial || ''} onChange={(e) => setScreenForm(f => ({ ...f, server_serial: e.target.value }))} placeholder="e.g. SN-12345678" />
            <TextField
              label="Server Firmware Version" fullWidth size="small"
              value={screenForm.server_firmware_version || ''}
              onChange={(e) => setScreenForm(f => ({ ...f, server_firmware_version: e.target.value }))}
              placeholder="e.g. IMS3000 v3.2.1, ICMP 2.10.0"
              helperText="Some KDM formats need a minimum firmware. Useful when ingest fails for a 'mysterious' reason."
            />
            <Typography variant="subtitle2">Auditorium capability (v10.6.1)</Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Audio System</InputLabel>
                  <Select
                    value={screenForm.audio_system_type || ''}
                    label="Audio System"
                    onChange={(e) => setScreenForm(f => ({ ...f, audio_system_type: e.target.value }))}
                  >
                    <MenuItem value=""><em>not specified</em></MenuItem>
                    <MenuItem value="Dolby Atmos">Dolby Atmos</MenuItem>
                    <MenuItem value="Dolby 7.1">Dolby 7.1</MenuItem>
                    <MenuItem value="Dolby 5.1">Dolby 5.1</MenuItem>
                    <MenuItem value="Auro 11.1">Auro 11.1</MenuItem>
                    <MenuItem value="Auro 3D">Auro 3D</MenuItem>
                    <MenuItem value="DTS:X">DTS:X</MenuItem>
                    <MenuItem value="SDDS">SDDS</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={6}>
                <FormControl fullWidth size="small" disabled={!screenForm.is_3d}>
                  <InputLabel>3D System</InputLabel>
                  <Select
                    value={screenForm.three_d_system || ''}
                    label="3D System"
                    onChange={(e) => setScreenForm(f => ({ ...f, three_d_system: e.target.value }))}
                  >
                    <MenuItem value=""><em>none / not 3D-capable</em></MenuItem>
                    <MenuItem value="RealD">RealD</MenuItem>
                    <MenuItem value="Dolby 3D">Dolby 3D</MenuItem>
                    <MenuItem value="IMAX 3D">IMAX 3D</MenuItem>
                    <MenuItem value="MasterImage">MasterImage</MenuItem>
                    <MenuItem value="Volfoni">Volfoni</MenuItem>
                    <MenuItem value="Xpand">Xpand</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Autocomplete
              multiple freeSolo size="small"
              options={['1.85 Flat', '2.39 Scope', '1.43 IMAX', '1.90 Full Container', '1.66', '4:3']}
              value={screenForm.aspect_ratios_supported || []}
              onChange={(_e, val) => setScreenForm(f => ({ ...f, aspect_ratios_supported: val }))}
              renderInput={(p) => <TextField {...p} label="Aspect Ratios Supported" placeholder="Pick or type" helperText="Add every ratio this auditorium can mask/show." />}
            />
            <Autocomplete
              multiple freeSolo size="small"
              options={['Open Caption', 'Closed Caption', 'HI track', 'VI track', 'CCAP', 'SDH']}
              value={screenForm.subtitle_capability || []}
              onChange={(_e, val) => setScreenForm(f => ({ ...f, subtitle_capability: val }))}
              renderInput={(p) => <TextField {...p} label="Subtitle / Accessibility Tracks" placeholder="Pick or type" helperText="Open vs closed caption, HI/VI accessibility tracks." />}
            />
            <TextField label="Notes" fullWidth multiline minRows={2} maxRows={6} value={screenForm.notes} onChange={(e) => setScreenForm(f => ({ ...f, notes: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { height: 'auto !important' } }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScreenDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveScreen} disabled={!screenForm.name}>{screenDialog?.screen ? 'Save' : 'Add Projector / Screen'}</Button>
        </DialogActions>
      </Dialog>

      {/* Certificate upload dialog */}
      <Dialog open={Boolean(certUpload)} onClose={() => { setCertUpload(null); setCertFile(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>
          {certUpload?.certificate_filename ? 'Replace Certificate' : 'Upload Certificate'}
          : {certUpload?.name}
        </DialogTitle>
        <DialogContent>
          {certUpload?.certificate_filename && (
            <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.5, fontSize: '0.78rem' }}>
              ✓ Cert already on file: <strong>{certUpload.certificate_filename}</strong>
              {certUpload.certificate_not_after && (
                <Box component="span" sx={{ display: 'block', color: 'text.secondary', mt: 0.25, fontSize: '0.72rem' }}>
                  Expires {new Date(certUpload.certificate_not_after).toLocaleDateString()}. Uploading replaces it on every DCP node.
                </Box>
              )}
            </Alert>
          )}
          <Box
            sx={{
              mt: 1,
              border: '2px dashed',
              borderColor: certFile ? 'success.main' : 'divider',
              borderRadius: 1,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 0.2s, background-color 0.2s',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const file = e.dataTransfer.files[0];
              if (file) setCertFile(file);
            }}
            onClick={() => document.getElementById('cert-file-input').click()}
          >
            <input
              id="cert-file-input"
              type="file"
              hidden
              accept=".pem,.zip,application/x-pem-file,application/x-x509-ca-cert,application/zip"
              onChange={(e) => setCertFile(e.target.files[0])}
            />
            <UploadIcon sx={{ fontSize: 40, color: certFile ? 'success.main' : 'text.secondary', mb: 1 }} />
            <Typography variant="body1" fontWeight={500}>
              {certFile ? certFile.name : 'Drop the .pem cert OR the vendor zip bundle'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Accepts .pem files and .zip bundles. For zip uploads we
              pick the SMPTE leaf automatically and archive the full
              bundle — you can fall back to a different cert later
              without re-asking the venue.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCertUpload(null); setCertFile(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleUploadCert} disabled={!certFile}>Upload & Push to Nodes</Button>
        </DialogActions>
      </Dialog>

      {/* Request link dialog */}
      <Dialog open={requestLinkDialog} onClose={() => setRequestLinkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Venue Request Form</DialogTitle>
        <DialogContent>
          {!requestLinkResult ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Creates a single-use, expiring link. The venue fills out their projection details, uploads their server certificate, and provides scheduling info.
              </Typography>
              <TextField label="Title / Purpose" fullWidth value={requestForm.title}
                onChange={(e) => setRequestForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Come What May — Festival Run 2026" />
              <Grid container spacing={2}>
                <Grid size={6}><TextField label="Recipient Name" fullWidth value={requestForm.recipient_name}
                  onChange={(e) => setRequestForm(f => ({ ...f, recipient_name: e.target.value }))} /></Grid>
                <Grid size={6}><TextField label="Recipient Email(s)" fullWidth value={requestForm.recipient_email}
                  onChange={(e) => setRequestForm(f => ({ ...f, recipient_email: e.target.value }))}
                  helperText="Comma-separate for multiple recipients" /></Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid size={12}><TextField label="CC" fullWidth value={requestForm.cc_email}
                  onChange={(e) => setRequestForm(f => ({ ...f, cc_email: e.target.value }))}
                  helperText="Optional. Comma-separate for multiple CC addresses. Venue can reply to this address with their certificate." /></Grid>
              </Grid>
              <TextField label="Message to Venue" fullWidth multiline minRows={3} maxRows={8}
                value={requestForm.message || ''}
                onChange={(e) => setRequestForm(f => ({ ...f, message: e.target.value }))}
                placeholder="e.g. Hi, we'll be delivering a DCP for Come What May. Please fill out the form below so we can generate your KDM and deliver the package."
                helperText="Included in the email to the venue. Explain what the request is for."
                sx={{ '& .MuiOutlinedInput-root': { height: 'auto !important' } }} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Screening dates (you fill these — venue does not need to)
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TextField label="First screening" type="datetime-local" fullWidth
                    InputLabelProps={{ shrink: true }} value={requestForm.first_screening_at}
                    onChange={(e) => setRequestForm(f => ({ ...f, first_screening_at: e.target.value }))} />
                </Grid>
                <Grid size={6}>
                  <TextField label="Last screening" type="datetime-local" fullWidth
                    InputLabelProps={{ shrink: true }} value={requestForm.last_screening_at}
                    onChange={(e) => setRequestForm(f => ({ ...f, last_screening_at: e.target.value }))} />
                </Grid>
              </Grid>
              {/* Optional pre-fill — collapsed by default. Counting
                  the populated fields lets the operator know "I have
                  4 things filled in for this invite" without expanding.
                  Required fields (title, recipient, screening dates)
                  stay above this Accordion so the common case is fast. */}
              {(() => {
                const filledCount = countPrefilledRequestFields(requestForm);
                return (
                  <Accordion variant="outlined" disableGutters sx={{ mt: 1, '&::before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        Optional pre-fill
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                        {filledCount > 0 ? `${filledCount} field${filledCount === 1 ? '' : 's'} filled` : 'None filled'}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Fill in anything you already know about the venue. The form will pre-populate
                  these fields and the venue just needs to verify, correct, and upload their
                  certificate. (None of these are required.)
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid size={6}>
                    <Autocomplete size="small" freeSolo
                      options={existingChains}
                      value={requestForm.chain || ''}
                      onChange={(e, value) => setRequestForm(f => ({ ...f, chain: value || '' }))}
                      onInputChange={(e, value) => setRequestForm(f => ({ ...f, chain: value || '' }))}
                      renderInput={(params) => (<TextField {...params} label="Chain" placeholder="e.g. AMC" />)}
                    />
                  </Grid>
                  <Grid size={6}><TextField size="small" label="Venue name" fullWidth value={requestForm.venue_name}
                    onChange={(e) => setRequestForm(f => ({ ...f, venue_name: e.target.value }))} /></Grid>
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Auditoriums / screens — add one row per room. The venue can add more or
                      remove these on their end.
                    </Typography>
                    {(requestForm.screen_names || ['']).map((sn, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField size="small" fullWidth placeholder={`e.g. Screen ${idx + 1}`} value={sn}
                          onChange={(e) => {
                            const next = [...(requestForm.screen_names || [''])];
                            next[idx] = e.target.value;
                            setRequestForm(f => ({ ...f, screen_names: next }));
                          }} />
                        {(requestForm.screen_names || []).length > 1 && (
                          <Button size="small" color="inherit" onClick={() => {
                            const next = (requestForm.screen_names || []).filter((_, i) => i !== idx);
                            setRequestForm(f => ({ ...f, screen_names: next.length ? next : [''] }));
                          }}>Remove</Button>
                        )}
                      </Box>
                    ))}
                    <Button size="small" variant="outlined" onClick={() => {
                      setRequestForm(f => ({ ...f, screen_names: [...(f.screen_names || ['']), ''] }));
                    }}>+ Add another auditorium</Button>
                  </Grid>
                  <Grid size={6}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={serverCatalog}
                      getOptionLabel={(o) => typeof o === 'string' ? o : serverLabel(o)}
                      filterOptions={(opts, { inputValue }) => filterEquipment(opts, inputValue)}
                      inputValue={requestForm.server_info || ''}
                      onInputChange={(e, value, reason) => {
                        if (reason === 'input') setRequestForm(f => ({ ...f, server_info: value }));
                      }}
                      onChange={(e, picked) => {
                        if (picked && typeof picked === 'object') {
                          setRequestForm(f => ({ ...f, server_info: `${picked.make} ${picked.model}` }));
                        }
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Media server (make + model)" placeholder="Dolby IMS3000, GDC SR-1000…" />
                      )}
                    />
                  </Grid>
                  <Grid size={6}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={projectorCatalog}
                      getOptionLabel={(o) => typeof o === 'string' ? o : projectorLabel(o)}
                      filterOptions={(opts, { inputValue }) => filterEquipment(opts, inputValue)}
                      inputValue={requestForm.projector_info || ''}
                      onInputChange={(e, value, reason) => {
                        if (reason === 'input') setRequestForm(f => ({ ...f, projector_info: value }));
                      }}
                      onChange={(e, picked) => {
                        if (picked && typeof picked === 'object') {
                          setRequestForm(f => ({ ...f, projector_info: `${picked.make} ${picked.model}` }));
                        }
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Projector (make + model)" placeholder="Barco SP4K-25, Christie CP4325-RGB…" />
                      )}
                    />
                  </Grid>
                  <Grid size={6}><TextField size="small" label="Server serial #" fullWidth value={requestForm.server_serial}
                    onChange={(e) => setRequestForm(f => ({ ...f, server_serial: e.target.value }))} /></Grid>
                  <Grid size={6}><TextField size="small" label="Contact phone" fullWidth value={requestForm.tech_contact_phone}
                    onChange={(e) => setRequestForm(f => ({ ...f, tech_contact_phone: e.target.value }))} /></Grid>
                  <Grid size={12}><TextField size="small" label="Street address" fullWidth value={requestForm.street_address}
                    onChange={(e) => setRequestForm(f => ({ ...f, street_address: e.target.value }))} /></Grid>
                  <Grid size={5}><TextField size="small" label="City" fullWidth value={requestForm.city}
                    onChange={(e) => setRequestForm(f => ({ ...f, city: e.target.value }))} /></Grid>
                  <Grid size={4}><TextField size="small" label="State" fullWidth value={requestForm.state}
                    onChange={(e) => setRequestForm(f => ({ ...f, state: e.target.value }))} /></Grid>
                  <Grid size={3}><TextField size="small" label="Country" fullWidth value={requestForm.country}
                    onChange={(e) => setRequestForm(f => ({ ...f, country: e.target.value }))} /></Grid>
                  <Grid size={3}><TextField size="small" label="Zip / Postal" fullWidth value={requestForm.zip_code}
                    onChange={(e) => setRequestForm(f => ({ ...f, zip_code: e.target.value }))} /></Grid>
                  <Grid size={6}>
                    <TextField size="small" label="Timezone" fullWidth select SelectProps={{ native: true }}
                      value={requestForm.timezone}
                      onChange={(e) => setRequestForm(f => ({ ...f, timezone: e.target.value }))}>
                      <option value=""></option>
                      <option value="America/Los_Angeles">Los Angeles</option>
                      <option value="America/Denver">Denver</option>
                      <option value="America/Chicago">Chicago</option>
                      <option value="America/New_York">New York</option>
                      <option value="America/Toronto">Toronto</option>
                      <option value="America/Mexico_City">Mexico City</option>
                      <option value="Europe/London">London</option>
                      <option value="Europe/Paris">Paris</option>
                      <option value="Europe/Berlin">Berlin</option>
                      <option value="Asia/Tokyo">Tokyo</option>
                      <option value="Australia/Sydney">Sydney</option>
                    </TextField>
                  </Grid>
                  <Grid size={3}>
                    <TextField size="small" label="Audio" fullWidth select SelectProps={{ native: true }}
                      value={requestForm.version}
                      onChange={(e) => setRequestForm(f => ({ ...f, version: e.target.value }))}>
                      <option value=""></option>
                      <option value="5.1">5.1</option>
                      <option value="stereo">Stereo</option>
                    </TextField>
                  </Grid>
                  <Grid size={3}>
                    <TextField size="small" label="Resolution" fullWidth select SelectProps={{ native: true }}
                      value={requestForm.resolution_required}
                      onChange={(e) => setRequestForm(f => ({ ...f, resolution_required: e.target.value }))}>
                      <option value=""></option>
                      <option value="2K">2K</option>
                      <option value="4K">4K</option>
                    </TextField>
                  </Grid>
                </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })()}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField label="Expires in (days)" type="number" value={requestForm.expires_days}
                  onChange={(e) => setRequestForm(f => ({ ...f, expires_days: parseInt(e.target.value) || 14 }))}
                  sx={{ maxWidth: 200 }} />
                <FormControlLabel
                  control={<Checkbox checked={requestForm.send_email}
                    onChange={(e) => setRequestForm(f => ({ ...f, send_email: e.target.checked }))} />}
                  label="Email link to recipient" />
              </Box>
            </Box>
          ) : requestLinkResult.error ? (
            <Alert severity="error">{requestLinkResult.error}</Alert>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Link created!
                {requestLinkResult.email_sent && ` Email sent to ${requestLinkResult.recipient_name || 'recipient'}.`}
                {requestLinkResult.email_error && ` Email failed: ${requestLinkResult.email_error}`}
              </Alert>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {requestLinkResult.url}
                </Typography>
                <Tooltip title={copied ? "Copied!" : "Copy link"}>
                  <IconButton size="small" onClick={() => handleCopy(requestLinkResult.url)}>
                    <CopyIcon fontSize="small" color={copied ? 'success' : 'action'} />
                  </IconButton>
                </Tooltip>
              </Box>
              {requestLinkResult.recipient_name && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  For: {requestLinkResult.recipient_name} — expires {new Date(requestLinkResult.expires_at).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestLinkDialog(false)}>Close</Button>
          {!requestLinkResult && (
            <Button variant="contained" startIcon={<SendIcon />} onClick={handleCreateRequestLink}>Create Link</Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>

      {/* Bulk-send dialog driven by the table-mode action bar (v10.19.50). */}
      <VenuesBulkRequestDialog
        open={bulkRequestDialog}
        venues={(venues || []).filter(v => tableSelection.includes(v.id))}
        onClose={() => setBulkRequestDialog(false)}
        onComplete={(results) => {
          const successCount = (results || []).filter(r => !r.error).length;
          const errorCount = (results || []).filter(r => r.error).length;
          setSnackbar({
            open: true,
            severity: errorCount > 0 ? 'warning' : 'success',
            message: errorCount > 0
              ? `Sent ${successCount} request${successCount === 1 ? '' : 's'}, ${errorCount} failed.`
              : `Sent ${successCount} request${successCount === 1 ? '' : 's'}.`,
          });
          // Refresh the outstanding-requests list so the operator sees
          // the new tokens without a manual reload.
          dispatch(fetchVenues({ includeArchived: showArchived }));
        }}
      />

      {/* Operator complete-with-cert dialog */}
      <Dialog open={Boolean(completeReqDialog)} onClose={() => !completeReqBusy && setCompleteReqDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete request with cert</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Use this when the venue replied via email with their .pem instead of using the public form.
            We'll archive the cert, push it to all DCP nodes, create the venue/screen, and mark the request submitted.
          </Typography>
          {completeReqError && <Alert severity="error" sx={{ mb: 2 }}>{completeReqError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" startIcon={<UploadIcon />} fullWidth>
                {completeReqForm.cert_file ? completeReqForm.cert_file.name : 'Attach .pem certificate'}
                <input
                  type="file"
                  hidden
                  accept=".pem,.crt,.cert,application/x-x509-ca-cert,application/x-pem-file"
                  onChange={(e) => setCompleteReqForm(f => ({ ...f, cert_file: e.target.files?.[0] || null }))}
                />
              </Button>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth size="small" label="Venue Name" required
                value={completeReqForm.venue_name}
                onChange={(e) => setCompleteReqForm(f => ({ ...f, venue_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Screen / Auditorium"
                value={completeReqForm.screen_name}
                onChange={(e) => setCompleteReqForm(f => ({ ...f, screen_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Chain"
                value={completeReqForm.chain}
                onChange={(e) => setCompleteReqForm(f => ({ ...f, chain: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="City"
                value={completeReqForm.city}
                onChange={(e) => setCompleteReqForm(f => ({ ...f, city: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Tech Contact"
                value={completeReqForm.tech_contact_name}
                onChange={(e) => setCompleteReqForm(f => ({ ...f, tech_contact_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Tech Contact Email"
                value={completeReqForm.tech_contact_email}
                onChange={(e) => setCompleteReqForm(f => ({ ...f, tech_contact_email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Operator note (optional, e.g. 'Cert received via email 5/2 from kip@beverly')"
                multiline minRows={2}
                value={completeReqForm.operator_note}
                onChange={(e) => setCompleteReqForm(f => ({ ...f, operator_note: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteReqDialog(null)} disabled={completeReqBusy}>Cancel</Button>
          <Button variant="contained" onClick={submitCompleteRequest} disabled={completeReqBusy}>
            {completeReqBusy ? 'Processing…' : 'Complete request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Venue Confirmation */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Archive Venue?</DialogTitle>
        <DialogContent>
          <Typography>
            Archive <strong>{deleteConfirm?.name}</strong>{deleteConfirm?.city ? ` (${deleteConfirm.city})` : ''}?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            The venue and its screens move out of the active catalog but stay
            in the system for reporting. KDM history, bookings, and audit
            entries are preserved. Restore from the "Show archived" toggle.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={async () => {
            await dispatch(deleteVenue(deleteConfirm.id));
            setDeleteConfirm(null);
            dispatch(fetchVenues({ includeArchived: showArchived }));
          }}>Archive</Button>
        </DialogActions>
      </Dialog>

      {venueComposer && (
        <EmailComposer
          open={!!venueComposer}
          onClose={() => setVenueComposer(null)}
          touchpoint="venue_followup"
          subjectId={venueComposer.venueId}
          contextLabel={venueComposer.label}
        />
      )}

      {venueOutcomeDialog && (
        <RecordOutcomeDialog
          open={!!venueOutcomeDialog}
          onClose={() => setVenueOutcomeDialog(null)}
          venueId={venueOutcomeDialog.venueId}
          venueLabel={venueOutcomeDialog.label}
          onSaved={() => {
            // Refresh the in-dialog outcomes timeline so the operator
            // sees the new entry without closing + reopening.
            if (venueDialog?.id) {
              api.get(`/api/venues/${venueDialog.id}/outcomes`)
                .then((r) => setVenueOutcomes(r.data || []))
                .catch(() => {});
            }
            setVenueOutcomeDialog(null);
          }}
        />
      )}
    </Box>
  );
}

// --- Venue card sub-component ---
// Self-contained contacts manager used inside the Edit Venue dialog.
// Loads its own contacts list off the API on mount so it picks up any
// changes the operator made elsewhere; saves each row immediately.
function VenueContactsEditor({ venueId }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // 'new' | row
  const [form, setForm] = useState({ role: 'tech_contact', role_other: '', name: '', email: '', phone: '', notes: '', is_primary: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const reload = async () => {
    if (!venueId) return;
    try {
      const { data } = await api.get(`/api/venues/${venueId}/contacts`);
      setList(data);
    } catch (_) { /* swallow — best-effort */ }
  };
  useEffect(() => { reload(); }, [venueId]);

  const openNew = () => {
    setForm({ role: 'tech_contact', role_other: '', name: '', email: '', phone: '', notes: '', is_primary: list.length === 0 });
    setErr(''); setEditing('new');
  };
  const openEdit = (c) => {
    setForm({
      role: c.role || 'tech_contact', role_other: c.role_other || '',
      name: c.name || '', email: c.email || '', phone: c.phone || '',
      notes: c.notes || '', is_primary: !!c.is_primary,
    });
    setErr(''); setEditing(c);
  };
  const save = async () => {
    if (!form.email && !form.name && !form.phone) {
      setErr('Provide at least one of name, email, or phone.');
      return;
    }
    setBusy(true);
    try {
      const body = { ...form };
      if (body.role !== 'other') body.role_other = '';
      if (editing === 'new') {
        await api.post(`/api/venues/${venueId}/contacts`, body);
      } else {
        await api.patch(`/api/venues/contacts/${editing.id}`, body);
      }
      setEditing(null);
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name || c.email}? They stay in history but no longer get emails.`)) return;
    try {
      await api.delete(`/api/venues/contacts/${c.id}`);
      await reload();
    } catch (_) { /* swallow — best-effort */ }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Contacts</Typography>
        <Chip size="small" label={list.length} sx={{ ml: 1 }} />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<AddIcon />} onClick={openNew}>Add Contact</Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
        Add as many contacts as you need — tech contact, projectionist, theater manager, etc. The "primary" contact for each role becomes the default To: in routed emails; everyone else goes to CC. The operator can always override on send.
      </Typography>
      <Table size="small" sx={{ mb: 1 }}>
        <TableBody>
          {list.length === 0 && (
            <TableRow><TableCell sx={{ py: 1 }} colSpan={5}>
              <Typography variant="caption" color="text.secondary">No contacts yet — click Add Contact to start.</Typography>
            </TableCell></TableRow>
          )}
          {list.map(c => (
            <TableRow key={c.id} hover>
              <TableCell sx={{ py: 0.5, width: 150 }}>
                <Chip
                  size="small"
                  label={c.role === 'other' ? (c.role_other || 'Other') : (ROLE_LABEL[c.role] || c.role)}
                  color={c.is_primary ? 'primary' : 'default'}
                  variant={c.is_primary ? 'filled' : 'outlined'}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </TableCell>
              <TableCell sx={{ py: 0.5 }}>
                <Typography variant="body2">{c.name || '—'}</Typography>
              </TableCell>
              <TableCell sx={{ py: 0.5 }}>
                <Typography variant="caption" color="text.secondary">{c.email || '—'}</Typography>
              </TableCell>
              <TableCell sx={{ py: 0.5, width: 130 }}>
                <Typography variant="caption" color="text.secondary">{c.phone || '—'}</Typography>
              </TableCell>
              <TableCell sx={{ py: 0.5, width: 80 }} align="right">
                <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(c)}><EditIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                <Tooltip title="Remove"><IconButton size="small" onClick={() => remove(c)}><DeleteIcon sx={{ fontSize: 14 }} color="error" /></IconButton></Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={Boolean(editing)} onClose={() => !busy && setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing === 'new' ? 'Add contact' : 'Edit contact'}</DialogTitle>
        <DialogContent>
          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <FormControl size="small">
              <InputLabel>Role</InputLabel>
              <Select label="Role" value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}>
                {ROLE_OPTIONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </Select>
            </FormControl>
            {form.role === 'other' && (
              <TextField size="small" label="Role label" value={form.role_other}
                onChange={(e) => setForm(f => ({ ...f, role_other: e.target.value }))} />
            )}
            <TextField size="small" label="Name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
            <TextField size="small" label="Email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
            <TextField size="small" label="Phone" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} />
            <TextField size="small" label="Notes" multiline rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
            <FormControlLabel
              control={<Checkbox checked={form.is_primary} onChange={(e) => setForm(f => ({ ...f, is_primary: e.target.checked }))} />}
              label="Primary for this role (becomes default To: in routed emails)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


const ROLE_OPTIONS = [
  { value: 'tech_contact', label: 'Tech Contact' },
  { value: 'projectionist', label: 'Projectionist' },
  { value: 'theater_manager', label: 'Theater Manager' },
  { value: 'general_manager', label: 'General Manager' },
  { value: 'booking', label: 'Booking' },
  { value: 'owner', label: 'Owner' },
  { value: 'accounting', label: 'Accounting' },
  { value: 'other', label: 'Other (specify)' },
];
const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map(r => [r.value, r.label]));

// Inline summary of a venue's delivery profile + last outcome. Shown
// when the venue card is expanded so the operator sees rating-style
// info at a glance without opening the edit dialog.
function VenueProfileSummary({ venue }) {
  const [last, setLast] = React.useState(null); // null = loading; [] = none; obj = found
  React.useEffect(() => {
    let cancelled = false;
    api.get(`/api/venues/${venue.id}/outcomes?limit=1`)
      .then((r) => { if (!cancelled) setLast((r.data || [])[0] || false); })
      .catch(() => { if (!cancelled) setLast(false); });
    return () => { cancelled = true; };
  }, [venue.id]);

  // Empty state: nothing set, no outcomes recorded — surface the "ask
  // first" prompt so the operator notices.
  const hasAnyProfile = !!(
    venue.preferred_format || venue.ingest_capability || venue.responsiveness
    || venue.kdm_method_preferred
  );
  const hasOutcomes = last && typeof last === 'object';

  if (!hasAnyProfile && !hasOutcomes) {
    return (
      <Box sx={{ mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1, border: 1, borderColor: 'warning.main', borderStyle: 'dashed' }}>
        <Typography variant="overline" sx={{ fontSize: '0.65rem', color: 'warning.main' }}>
          Delivery profile — not yet rated
        </Typography>
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary', fontSize: '0.72rem', mt: 0.25 }}>
          We don't know this venue's delivery preferences yet. Worth asking what archive format / KDM method they prefer before sending. After you record the first outcome, this section will start populating.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
      <Typography variant="overline" sx={{ fontSize: '0.65rem' }} color="text.secondary">
        Delivery profile
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
        {venue.preferred_format && (
          <Chip size="small" label={`Format: .${venue.preferred_format}`} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        )}
        {venue.ingest_capability && (
          <Chip size="small" label={`Capability: ${venue.ingest_capability.replace(/_/g, ' ')}`} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        )}
        {venue.responsiveness && (
          <Chip size="small" label={`Replies: ${venue.responsiveness}`}
            color={venue.responsiveness === 'unresponsive' ? 'warning' : venue.responsiveness === 'quick' ? 'success' : 'default'}
            variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        )}
        {venue.kdm_method_preferred && (
          <Chip size="small" label={`KDM: ${venue.kdm_method_preferred.replace(/_/g, ' ')}`} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        )}
        {!hasAnyProfile && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            (no profile fields set — open Edit Venue to add)
          </Typography>
        )}
      </Box>
      {hasOutcomes && (
        <Box sx={{ mt: 1, fontSize: '0.7rem', color: 'text.secondary' }}>
          <strong>Last delivery</strong> ({new Date(last.created_at).toLocaleDateString()}):{' '}
          <Box component="span" sx={{ fontWeight: 500 }}>.{last.format_used || '?'}</Box>{' → '}
          <Box component="span" sx={{
            color: last.outcome === 'success_clean' ? 'success.main'
              : ['format_unknown','download_failed','kdm_filter_blocked','av_issue'].includes(last.outcome) ? 'error.main'
              : 'warning.main',
          }}>{last.outcome.replace(/_/g, ' ')}</Box>
          {last.notes && <Box component="div" sx={{ pl: 2, fontStyle: 'italic' }}>— {last.notes.slice(0, 140)}{last.notes.length > 140 ? '…' : ''}</Box>}
        </Box>
      )}
    </Box>
  );
}

/**
 * Compact chip that shows the cert's expiry status; click opens a popover
 * with the structured details we extracted at upload time (subject CN,
 * issuer, roles, thumbprint, validity window, key usage). Replaces the
 * old "✓ filename-in-tooltip" chip.
 */
function CertChip({ screen, onUploadCert }) {
  const [anchor, setAnchor] = useState(null);
  if (!screen.certificate_filename) {
    return screen.playback_type === 'mp4' ? (
      <Tooltip title="MP4-only screen — no certificate needed" arrow>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>n/a</Typography>
      </Tooltip>
    ) : (
      <Chip label="missing" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
    );
  }
  const expiry = formatCertExpiry(screen.certificate_not_after);
  const issuer = describeCertIssuer(screen.certificate_issuer_cn);
  const roles = Array.isArray(screen.certificate_roles) ? screen.certificate_roles : [];
  // Build a clear "uploaded · <expiry>" label so the operator can tell
  // at a glance that the cert IS on file. Pre-v10.6.1 the chip just
  // read "10y+" or "30d" — accurate but readable as "what does this
  // duration even mean?" Now: green ✓ leads, expiry follows.
  const isExpired = expiry.severity === 'expired';
  const chipLabel = isExpired
    ? `EXPIRED · ${expiry.label}`
    : (expiry.severity === 'unknown' ? '✓ on file' : `✓ ${expiry.label}`);
  const tooltipTitle = expiry.severity === 'unknown'
    ? `${screen.certificate_filename} — uploaded (legacy, re-upload to populate metadata)`
    : `✓ Cert on file · expires ${formatCertDate(screen.certificate_not_after)}${issuer ? ` · ${issuer}` : ''}`;
  return (
    <>
      <Tooltip title={tooltipTitle} arrow placement="top">
        <Chip
          label={chipLabel}
          size="small"
          color={expiry.color}
          variant={expiry.severity === 'ok' || expiry.severity === 'unknown' ? 'filled' : 'filled'}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22, minWidth: 76, cursor: 'pointer' }}
        />
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 480, fontSize: '0.78rem' } } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          Certificate details
          <Chip label={expiry.label} size="small" color={expiry.color} sx={{ height: 18, fontSize: '0.62rem' }} />
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">Subject</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {screen.certificate_subject_cn || '—'}
          </Typography>

          <Typography variant="caption" color="text.secondary">Issuer</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            {screen.certificate_issuer_cn || '—'}
            {issuer && screen.certificate_issuer_cn && issuer !== screen.certificate_issuer_cn && (
              <> ({issuer})</>
            )}
          </Typography>

          <Typography variant="caption" color="text.secondary">Roles</Typography>
          <Box>
            {roles.length === 0
              ? <Typography variant="caption" color="text.secondary">none detected</Typography>
              : roles.map(r => (
                  <Chip key={r} label={r} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', mr: 0.5 }} />
                ))
            }
          </Box>

          <Typography variant="caption" color="text.secondary">Valid from</Typography>
          <Typography variant="caption">{formatCertDate(screen.certificate_not_before)}</Typography>

          <Typography variant="caption" color="text.secondary">Valid to</Typography>
          <Typography variant="caption" sx={{ color: expiry.severity === 'ok' ? 'inherit' : `${expiry.color}.main`, fontWeight: 600 }}>
            {formatCertDate(screen.certificate_not_after)}
            {expiry.daysRemaining != null && expiry.daysRemaining >= 0 && (
              <> ({expiry.daysRemaining} day{expiry.daysRemaining === 1 ? '' : 's'} remaining)</>
            )}
          </Typography>

          <Typography variant="caption" color="text.secondary">Serial</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            {screen.certificate_serial || '—'}
          </Typography>

          <Typography variant="caption" color="text.secondary">SHA-1 thumbprint</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem', wordBreak: 'break-all' }}>
            {screen.certificate_thumbprint || '—'}
          </Typography>

          {screen.certificate_thumbprint_sha256 && (
            <>
              <Typography variant="caption" color="text.secondary">SHA-256 thumbprint</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', wordBreak: 'break-all' }}>
                {screen.certificate_thumbprint_sha256}
              </Typography>
            </>
          )}

          {(screen.certificate_public_key_algorithm || screen.certificate_public_key_size_bits) && (
            <>
              <Typography variant="caption" color="text.secondary">Public key</Typography>
              <Typography variant="caption">
                {[screen.certificate_public_key_algorithm, screen.certificate_public_key_size_bits ? `${screen.certificate_public_key_size_bits}-bit` : null].filter(Boolean).join(' · ')}
              </Typography>
            </>
          )}

          {screen.certificate_signature_algorithm && (
            <>
              <Typography variant="caption" color="text.secondary">Signature</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
                {screen.certificate_signature_algorithm}
              </Typography>
            </>
          )}

          {screen.certificate_dn_qualifier && (
            <>
              <Typography variant="caption" color="text.secondary">dnQualifier</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', wordBreak: 'break-all' }}>
                {screen.certificate_dn_qualifier}
              </Typography>
            </>
          )}

          {Array.isArray(screen.certificate_key_usage) && screen.certificate_key_usage.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary">Key usage</Typography>
              <Box>
                {screen.certificate_key_usage.map(k => (
                  <Chip key={k} label={k} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.58rem', mr: 0.5 }} />
                ))}
              </Box>
            </>
          )}
          <Typography variant="caption" color="text.secondary">File</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
            {screen.certificate_filename}
            {screen.certificate_bundle_filename && screen.certificate_bundle_filename !== screen.certificate_filename && (
              <Box component="span" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.6rem', mt: 0.25 }}>
                from bundle: {screen.certificate_bundle_filename}
              </Box>
            )}
          </Typography>
        </Box>
        {screen.bundle_readme_text && (
          <Box sx={{ mt: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 0.5, maxHeight: 160, overflow: 'auto' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
              Bundle readme
            </Typography>
            <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.62rem', lineHeight: 1.4 }}>
              {screen.bundle_readme_text}
            </Typography>
          </Box>
        )}
        {(expiry.severity === 'critical' || expiry.severity === 'expired') && (
          <Alert severity="error" sx={{ mt: 1.5, py: 0.5, fontSize: '0.72rem' }}>
            {expiry.severity === 'expired'
              ? 'KDMs encrypted to this cert will be rejected at ingest. Request a fresh cert from the venue.'
              : 'KDM ingest will start failing soon. Request a fresh cert from the venue.'}
          </Alert>
        )}
        {onUploadCert && (
          <Button
            size="small"
            sx={{ mt: 1.5 }}
            onClick={() => { setAnchor(null); onUploadCert(screen); }}
          >
            Replace certificate
          </Button>
        )}
      </Popover>
    </>
  );
}

function VenueCard({ venue, onEdit, onRecordOutcome, onAddScreen, onInviteToAddScreen, onEditScreen, onUploadCert, onDelete, onRestore, onPurge, onDeleteScreen, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(!!defaultExpanded);
  const [contacts, setContacts] = useState(venue.contacts || []);
  const [contactDialog, setContactDialog] = useState(null); // 'new' | contact obj
  const [contactForm, setContactForm] = useState({ role: 'tech_contact', role_other: '', name: '', email: '', phone: '', notes: '', is_primary: false });
  const [contactsExpanded, setContactsExpanded] = useState(false);
  const [contactBusy, setContactBusy] = useState(false);
  const [contactError, setContactError] = useState('');

  useEffect(() => { setContacts(venue.contacts || []); }, [venue.contacts]);

  const reloadContacts = async () => {
    try {
      const { data } = await api.get(`/api/venues/${venue.id}/contacts`);
      setContacts(data);
    } catch (_) { /* swallow — best-effort */ }
  };

  const openNewContact = () => {
    setContactForm({ role: 'tech_contact', role_other: '', name: '', email: '', phone: '', notes: '', is_primary: contacts.length === 0 });
    setContactError('');
    setContactDialog('new');
  };
  const openEditContact = (c) => {
    setContactForm({
      role: c.role || 'tech_contact', role_other: c.role_other || '',
      name: c.name || '', email: c.email || '', phone: c.phone || '',
      notes: c.notes || '', is_primary: !!c.is_primary,
    });
    setContactError('');
    setContactDialog(c);
  };
  const saveContact = async () => {
    if (!contactForm.email && !contactForm.name && !contactForm.phone) {
      setContactError('Provide at least one of name, email, or phone.');
      return;
    }
    setContactBusy(true);
    try {
      const body = { ...contactForm };
      if (body.role !== 'other') body.role_other = '';
      if (contactDialog === 'new') {
        await api.post(`/api/venues/${venue.id}/contacts`, body);
      } else {
        await api.patch(`/api/venues/contacts/${contactDialog.id}`, body);
      }
      setContactDialog(null);
      await reloadContacts();
    } catch (e) {
      setContactError(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setContactBusy(false);
    }
  };
  const deleteContact = async (c) => {
    if (!window.confirm(`Remove ${c.name || c.email}? They'll stay in history but no longer get emails.`)) return;
    try {
      await api.delete(`/api/venues/contacts/${c.id}`);
      await reloadContacts();
    } catch (_) { /* swallow — best-effort */ }
  };

  // Group contacts by role for the rollup display.
  const contactsByRole = contacts.reduce((acc, c) => {
    const k = c.role || 'tech_contact';
    if (!acc[k]) acc[k] = [];
    acc[k].push(c);
    return acc;
  }, {});

  const screenCount = (venue.screens || []).filter(s => s.is_active).length;
  // Cert rollups exclude MP4-only screens — they don't need a cert,
  // so a venue with just an MP4 room shouldn't read "no certs" or
  // count as missing certs.
  const dcpScreens = (venue.screens || []).filter(s => s.is_active && (s.playback_type || 'dcp') !== 'mp4');
  const certCount = dcpScreens.filter(s => s.certificate_filename).length;
  const dcpCount = dcpScreens.length;
  const mp4Count = (venue.screens || []).filter(s => s.is_active && s.playback_type === 'mp4').length;

  return (
    <Paper sx={{ m: 0.5, p: expanded ? 2 : 1.25 }} variant="outlined">
      {/* Compact header — single row by default. Click to expand and
          see the screens table + contacts editor. With 100+ venues
          on the page, the headline + chips are enough to scan. */}
      <Box
        onClick={() => setExpanded(e => !e)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          cursor: 'pointer', userSelect: 'none',
          '&:hover': { bgcolor: 'action.hover' },
          mx: -1, px: 1, py: 0.5, borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 18 }}>
          {expanded ? <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon fontSize="small" sx={{ transform: 'rotate(-90deg)', color: 'text.secondary' }} />}
        </Box>
        <TheaterIcon fontSize="small" color="primary" />
        <Typography variant="subtitle1" fontWeight={600} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{venue.name}</Typography>
        {venue.city && <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>— {venue.city}{venue.state ? `, ${venue.state}` : ''}</Typography>}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip
          title={screenCount === 0
            ? 'No screens — distribution will fail. Click "Add screen" inside this card or upload a venue cert.'
            : `${screenCount} active screen${screenCount === 1 ? '' : 's'}`}
          arrow
        >
          <Chip
            size="small"
            label={screenCount === 0 ? 'no screens' : `${screenCount} screen${screenCount === 1 ? '' : 's'}`}
            color={screenCount === 0 ? 'warning' : 'default'}
            variant={screenCount === 0 ? 'filled' : 'outlined'}
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        </Tooltip>
        {mp4Count > 0 && (
          <Tooltip title={`${mp4Count} MP4-only screen${mp4Count === 1 ? '' : 's'} — accepts watermarked MP4 delivery, no cert/KDM needed`}>
            <Chip size="small" label={`${mp4Count} MP4`} color="info" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
          </Tooltip>
        )}
        {dcpCount > 0 && (certCount > 0 ? (
          <Chip size="small" label={`${certCount}/${dcpCount} cert${dcpCount === 1 ? '' : 's'}`}
            color={certCount === dcpCount ? 'success' : 'warning'}
            variant={certCount === dcpCount ? 'outlined' : 'filled'}
            sx={{ height: 18, fontSize: '0.65rem' }} />
        ) : (
          <Chip size="small" label="no certs" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
        ))}
        <Chip size="small" label={`${contacts.length} contact${contacts.length === 1 ? '' : 's'}`} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
        {/* "Last delivered" chip — operator scan target. Backend
            (v10.4.27+) ships latest KDM created_at + DCP name across
            all of the venue's screens, scoped to the active company.
            Hidden when no KDM history exists yet. */}
        {venue.last_kdm_at && (
          <Tooltip
            arrow
            title={
              venue.last_kdm_dcp_name
                ? `Last KDM issued — ${venue.last_kdm_dcp_name} (${new Date(venue.last_kdm_at).toLocaleString()})`
                : `Last KDM issued ${new Date(venue.last_kdm_at).toLocaleString()}`
            }
          >
            <Chip
              size="small"
              label={`Last: ${formatRelativeTime(venue.last_kdm_at)}`}
              color="success"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Tooltip>
        )}
        {/* Delivery-profile chips — collapsed-only summary so the operator
            can scan a long venue list without expanding each card. When
            the card IS expanded, the structured Delivery-profile panel
            below shows the same data labeled, so we hide these to avoid
            duplicate UI. */}
        {!expanded && venue.preferred_format && (
          <Tooltip title="Preferred archive format" arrow>
            <Chip size="small" label={`.${venue.preferred_format}`} color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
          </Tooltip>
        )}
        {!expanded && venue.ingest_capability && (
          <Tooltip title={
            venue.ingest_capability === 'low_engagement'
              ? "Low engagement — won't troubleshoot; ship something that just works"
              : `Ingest capability: ${venue.ingest_capability.replace(/_/g, ' ')}`
          } arrow>
            <Chip size="small" label={
              venue.ingest_capability === 'pro' ? 'Pro IT'
                : venue.ingest_capability === 'standard' ? 'Standard'
                : venue.ingest_capability === 'basic' ? 'Basic'
                : venue.ingest_capability === 'needs_handholding' ? 'Hand-hold'
                : venue.ingest_capability === 'low_engagement' ? 'Low engagement'
                : venue.ingest_capability
            } color={
              venue.ingest_capability === 'pro' ? 'success'
                : venue.ingest_capability === 'low_engagement' ? 'error'
                : venue.ingest_capability === 'needs_handholding' ? 'warning'
                : 'default'
            } variant={venue.ingest_capability === 'low_engagement' ? 'filled' : 'outlined'}
              sx={{ height: 18, fontSize: '0.65rem' }} />
          </Tooltip>
        )}
        {!expanded && venue.responsiveness === 'unresponsive' && (
          <Tooltip title="Historically unresponsive — pre-call recommended" arrow>
            <Chip size="small" label="slow reply" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
          </Tooltip>
        )}
        {!expanded && venue.responsiveness === 'quick' && (
          <Tooltip title="Quick to respond" arrow>
            <Chip size="small" label="quick" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
          </Tooltip>
        )}
        {venue.deleted_at && (
          <Chip size="small" label="Archived" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
        )}
        {/* Action icons inline on the header row, always visible, no
            floating-buttons-above-content. e.stopPropagation prevents
            the header click from also collapsing the row. */}
        {/* v11.0.0 — visible labels on the action buttons. The audit
            called out that the icon-only +, ▶, 📋, ✏️, 🗑 row required
            hover to understand each one. Buttons now carry text +
            icon; aria-label still set explicitly so screen readers
            and keyboard navigation work. The collapsed expand state
            still hides them — they only render when the operator has
            opened the venue card. */}
        <Box sx={{ display: 'flex', gap: 0.5, ml: 0.5, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
          {!venue.deleted_at && (
            <>
              <Tooltip title="Add a projector / screen (one auditorium's server + cert)">
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAddScreen}
                        aria-label="Add screen" sx={{ fontSize: '0.7rem', py: 0.25 }}>
                  Add screen
                </Button>
              </Tooltip>
              <Tooltip title="Email the venue a pre-filled request form so they can add a screen + cert themselves">
                <Button size="small" variant="text" startIcon={<SendIcon />} onClick={onInviteToAddScreen}
                        aria-label="Invite to add screen" sx={{ fontSize: '0.7rem', py: 0.25 }}>
                  Invite
                </Button>
              </Tooltip>
              {venue.editable !== false ? (
                <>
                  <Tooltip title="Log how a delivery went — format used, result, notes. Visible to the next operator.">
                    <Button size="small" variant="text" startIcon={<OutcomeIcon />} onClick={onRecordOutcome}
                            aria-label="Record outcome" sx={{ fontSize: '0.7rem', py: 0.25 }}>
                      Outcome
                    </Button>
                  </Tooltip>
                  <Tooltip title="Edit venue name, address, contacts, format preferences">
                    <Button size="small" variant="text" startIcon={<EditIcon />} onClick={onEdit}
                            aria-label="Edit venue" sx={{ fontSize: '0.7rem', py: 0.25 }}>
                      Edit
                    </Button>
                  </Tooltip>
                  <Tooltip title="Archive (preserves history; restorable)">
                    <Button size="small" variant="text" startIcon={<DeleteIcon />} onClick={onDelete}
                            aria-label="Archive venue" sx={{ fontSize: '0.7rem', py: 0.25, color: 'text.secondary' }}>
                      Archive
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="Venue's name/address owned by another workspace. Screens, contacts, and certs are shared — anyone can manage those.">
                  <Chip size="small" label="Shared" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                </Tooltip>
              )}
            </>
          )}
          {venue.deleted_at && venue.editable !== false && (
            <>
              <Tooltip title="Restore from archive — venue rejoins the active list">
                <Button size="small" variant="outlined" startIcon={<RestoreIcon />} onClick={onRestore}
                        aria-label="Restore venue" sx={{ fontSize: '0.7rem', py: 0.25, color: '#43a047', borderColor: '#43a047' }}>
                  Restore
                </Button>
              </Tooltip>
              <Tooltip title="Permanently delete (cannot be undone)">
                <Button size="small" variant="text" color="error" startIcon={<DeleteForeverIcon />} onClick={onPurge}
                        aria-label="Permanently delete venue" sx={{ fontSize: '0.7rem', py: 0.25 }}>
                  Delete
                </Button>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {/* Delivery profile summary — only when row expanded. Renders
          even when fields are empty (with an "ask the venue what
          they prefer" prompt) so the operator sees we DON'T know yet
          and can act on it. */}
      {expanded && (
        <VenueProfileSummary venue={venue} />
      )}

      {/* Contacts rollup — only when row expanded */}
      {expanded && (
      <Box sx={{ mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => setContactsExpanded(v => !v)}
        >
          <Typography variant="overline" sx={{ fontSize: '0.65rem' }} color="text.secondary">
            Contacts ({contacts.length})
          </Typography>
          {!contactsExpanded && Object.entries(contactsByRole).slice(0, 4).map(([role, cs]) => (
            <Chip
              key={role}
              size="small"
              label={`${ROLE_LABEL[role] || role}: ${cs.length}`}
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          ))}
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Add contact"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openNewContact(); }}><AddIcon fontSize="small" color="primary" /></IconButton></Tooltip>
        </Box>
        {contactsExpanded && (
          <Table size="small" sx={{ mt: 1 }}>
            <TableBody>
              {contacts.length === 0 && (
                <TableRow><TableCell sx={{ py: 0.5 }} colSpan={5}>
                  <Typography variant="caption" color="text.secondary">No contacts yet.</Typography>
                </TableCell></TableRow>
              )}
              {contacts.map(c => (
                <TableRow key={c.id} hover>
                  <TableCell sx={{ py: 0.5, width: 140 }}>
                    <Chip
                      size="small"
                      label={c.role === 'other' ? (c.role_other || 'Other') : (ROLE_LABEL[c.role] || c.role)}
                      color={c.is_primary ? 'primary' : 'default'}
                      variant={c.is_primary ? 'filled' : 'outlined'}
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption" fontWeight={500}>{c.name || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.email || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5, width: 130 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.phone || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5, width: 80 }} align="right">
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditContact(c)}><EditIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                    <Tooltip title="Remove"><IconButton size="small" onClick={() => deleteContact(c)}><DeleteIcon sx={{ fontSize: 14 }} color="error" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
      )}

      <Dialog open={Boolean(contactDialog)} onClose={() => !contactBusy && setContactDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{contactDialog === 'new' ? 'Add contact' : 'Edit contact'}</DialogTitle>
        <DialogContent>
          {contactError && <Alert severity="error" sx={{ mb: 2 }}>{contactError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <FormControl size="small">
              <InputLabel>Role</InputLabel>
              <Select label="Role" value={contactForm.role} onChange={(e) => setContactForm(f => ({ ...f, role: e.target.value }))}>
                {ROLE_OPTIONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </Select>
            </FormControl>
            {contactForm.role === 'other' && (
              <TextField size="small" label="Role label" value={contactForm.role_other}
                onChange={(e) => setContactForm(f => ({ ...f, role_other: e.target.value }))} />
            )}
            <TextField size="small" label="Name" value={contactForm.name}
              onChange={(e) => setContactForm(f => ({ ...f, name: e.target.value }))} />
            <TextField size="small" label="Email" value={contactForm.email}
              onChange={(e) => setContactForm(f => ({ ...f, email: e.target.value }))} />
            <TextField size="small" label="Phone" value={contactForm.phone}
              onChange={(e) => setContactForm(f => ({ ...f, phone: e.target.value }))} />
            <TextField size="small" label="Notes" multiline rows={2} value={contactForm.notes}
              onChange={(e) => setContactForm(f => ({ ...f, notes: e.target.value }))} />
            <FormControlLabel
              control={<Checkbox checked={contactForm.is_primary}
                onChange={(e) => setContactForm(f => ({ ...f, is_primary: e.target.checked }))} />}
              label="Primary for this role (becomes default To: in routed emails)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialog(null)} disabled={contactBusy}>Cancel</Button>
          <Button variant="contained" onClick={saveContact} disabled={contactBusy}>
            {contactBusy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {expanded && (venue.screens?.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Screen</TableCell>
                <TableCell>Projector</TableCell>
                <TableCell>Server</TableCell>
                <TableCell sx={{ width: 80 }}>Cert</TableCell>
                <TableCell align="right" sx={{ width: 130 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {venue.screens.filter(s => s.is_active).map(screen => {
                // Audio inline as a small chip on the Screen cell so we
                // can drop the dedicated column. Atmos/7.1 are the only
                // values worth seeing at a glance — 5.1 is the boring
                // default and gets a muted treatment.
                const audioLabel = { 2: 'Stereo', 6: '5.1', 8: '7.1', 16: 'Atmos' }[screen.max_audio_channels] || `${screen.max_audio_channels || 8}ch`;
                const audioInteresting = audioLabel !== '5.1';
                // Projector display: drop the leading dash when there's
                // no make. Only show make+model OR resolution OR both —
                // never a bare "— 2K".
                const projMake = (screen.projector_make || '').trim();
                const projModel = (screen.projector_model || '').trim();
                const projText = [projMake, projModel].filter(Boolean).join(' ');
                const srvMake = (screen.server_make || '').trim();
                const srvModel = (screen.server_model || '').trim();
                const srvText = [srvMake, srvModel].filter(Boolean).join(' ');
                return (
                <TableRow key={screen.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <ScreenIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={500}>{screen.name}</Typography>
                      {screen.playback_type === 'mp4' && (
                        <Chip label="MP4" size="small" color="info" variant="filled" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                      {screen.playback_type === 'both' && (
                        <Chip label="DCP + MP4" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                      {screen.screen_type && <Chip label={screen.screen_type} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />}
                      {screen.is_3d && <Chip label="3D" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
                      {screen.is_laser && <Chip label="Laser" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />}
                      {audioInteresting && <Chip label={audioLabel} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {projText && <Typography variant="body2">{projText}</Typography>}
                      {screen.projector_resolution && <Chip label={screen.projector_resolution} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />}
                      {!projText && !screen.projector_resolution && <Typography variant="body2" color="text.secondary">—</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {srvText || screen.server_serial ? (
                      <Tooltip title={screen.server_serial ? `S/N: ${screen.server_serial}` : ''} arrow>
                        <Typography variant="body2">
                          {srvText || <Box component="span" sx={{ color: 'text.secondary' }}>—</Box>}
                          {screen.server_serial && (
                            <Box component="span" sx={{ ml: 0.5, fontSize: '0.65rem', color: 'text.secondary', fontFamily: 'monospace' }}>· S/N</Box>
                          )}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ width: 96 }}>
                    <CertChip screen={screen} onUploadCert={onUploadCert} />
                  </TableCell>
                  <TableCell align="right">
                    {/* Shared catalog — any venue_manager can manage
                        screens / certs on any venue. The role gate
                        upstream is the access boundary. */}
                    <Tooltip title="Upload Certificate">
                      <IconButton size="small" onClick={() => onUploadCert(screen)}>
                        <UploadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Screen">
                      <IconButton size="small" onClick={() => onEditScreen(screen)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Screen">
                      <IconButton size="small" color="error" onClick={() => onDeleteScreen(screen)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>No screens added yet.</Typography>
      ))}
    </Paper>
  );
}

export default VenueCertificates;
